/**
 * /integrated — used inside an iframe in app.zenpost.ai.
 *
 * Flow:
 *  1. Read `token` and `app_language` from URL.
 *  2. POST /api/integrated/session -> validate + fetch billing snapshot.
 *  3. Render billing management UI — current plan pre-selected, customer picks changes.
 *  4. After any successful change, postMessage ZENPOST_BILLING_UPDATED to parent.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { CreditCard, Gift, X, Zap } from "lucide-react";
import { api, ApiError, apiErrorCode, formatMoney } from "../lib/api.js";
import { PhoneInput } from "../components/PhoneInput.js";
import { CpfInput } from "../components/CpfInput.js";
import { CouponSection } from "../components/CouponSection.js";
import { PaymentStickyBar } from "../components/PaymentStickyBar.js";
import { ResumePixBanner } from "../components/ResumePixBanner.js";
import { BrandLoader } from "../components/BrandLoader.js";
import { IntegratedPixPanel } from "../components/IntegratedPixPanel.js";
import { KeepPixSubscriptionDialog, type PixKeepState } from "../components/KeepPixSubscriptionDialog.js";
import { CardUpdatePanel } from "../components/CardUpdatePanel.js";
import { StoneCardForm } from "../components/StoneCardForm.js";
import { EMPTY_STONE_CARD, invalidStoneCardField, tokenizeStoneCard, type StoneCardFields } from "../lib/stoneCard.js";
import { IntegratedStripePanel } from "../components/IntegratedStripePanel.js";
import {
  PlanComparisonTable,
  PlanIntervalToggle,
} from "../components/plans/PlanComparisonTable.js";
import { isValidCPF, formatCPF } from "../../../shared/validation.js";
import { isPixAvailableForPhone, isValidPhoneE164, phoneUnusableForPix } from "../../../shared/phone.js";
import {
  getStoredActivePixOrderId,
  isPixOrderActive,
  pendingCheckoutMatchesSelection,
  savePixCheckoutContext,
  type PixOrderSnapshot,
} from "../lib/pixCheckout.js";
import { saveStripeCheckoutContext, saveStripeCheckoutSession } from "../lib/stripeCheckout.js";
import {
  isEmbeddedBillingContext,
  notifyParentBillingClose,
  notifyParentBillingUpdated,
} from "../lib/billingPostMessage.js";
import {
  BILLING_SUPPORT_WHATSAPP_DISPLAY,
  BILLING_SUPPORT_WHATSAPP_PHONE,
  buildBillingSupportWhatsAppUrl,
  buildWhatsAppUrl,
  type BillingSupportIntent,
} from "../lib/billingSupport.js";
import { isPixUnavailableError, logApiError } from "../lib/apiErrors.js";
import { clarityEvent } from "../lib/clarity.js";
import {
  MISSING_TOKEN_LOAD_ERROR,
  classifyIntegratedLoadError,
  integratedLoadErrorEventName,
  readPremiumTrialOfferToken,
  withOfferSessionRetry,
  type IntegratedLoadError,
} from "../lib/integratedLoadError.js";
import { computeIntegratedDueTodayCents, isScheduledDecision, previewMatchesSelection } from "../lib/integratedDueToday.js";
import { PremiumTrialPanel, premiumTrialGiveUpOpen } from "../components/PremiumTrialPanel.js";
import { premiumTrialGiveUpClosesAt, type PremiumTrialPageState } from "../../../shared/premium_trial.js";
import { formatBillingDate, formatBillingDayAndTime } from "../lib/formatBillingDate.js";
import { formatPaymentDescription } from "../lib/formatPaymentDescription.js";
import {
  bonusLabel,
  buildCancellationLosses,
  type CancelRescueAssets,
  type CancelRescueBonus,
  type CancelRescueZenlinksPage,
} from "../lib/cancellationLosses.js";
import {
  formatSlotClock,
  formatSupportSlotTime,
  groupSupportSlotsByDay,
  type SupportSlot,
} from "../lib/supportSlots.js";
import { SupportBookingCard } from "../lib/SupportBookingCard.js";
import { RetentionLadder } from "../components/RetentionLadder.js";
import {
  retentionLadderSteps,
  type RetentionLadderOffers,
  type RetentionLadderStep,
} from "../../../shared/retention_ladder.js";
import {
  showsRetentionGift,
  type RetentionGiftState,
  type RetentionOffer,
} from "../lib/retentionGift.js";
import {
  mayShowRefundWindowSpecialist,
  REFUND_WINDOW_SPECIALIST_KIND,
  showsRefundWindowSpecialist,
} from "../lib/refundWindowSpecialist.js";
import {
  cheaperPlanTargets,
  isCheaperPlanOffer,
  isInstallmentOffer,
  planChangePath,
  reasonStepAction,
  showsCheaperPlanOffer,
  type CheaperPlanCurrent,
  type CheaperPlanTarget,
  type PlanChangePath,
} from "../lib/cheaperPlanOffer.js";
import {
  CANCELLATION_REASONS,
  hasCancellationDetails,
  leadsToPlanPicker,
  normalizeSubreasons,
  rescueForReason,
  offersCheaperPlan,
  subreasonsFor,
  type CancellationChoice,
  type CancellationReason,
} from "../../../shared/cancellation_reasons.js";
import {
  describeScheduledChange,
  describeAppliedScheduledChange,
  describeRequestedChange,
  findMatchingScheduledChange,
  getCancelScheduledChangeActionLabel,
  getScheduledChangeSupportCopy,
  localSubscriptionMatchesAppliedChange,
} from "../lib/scheduledChangeLabel.js";
import { canSelfServiceReverseScheduledCancellation } from "../../../shared/scheduled_change.js";
import { subscriptionEnded } from "../../../shared/expired_billing.js";
import type { Plan, BillingInterval, PaymentMethod, PricingBreakdown, ChangePreview } from "../../../shared/types.js";
import { PLANS, getAnnualSavings, isPixAvailableForLocale, currencyFromLocale, getPlanPricing } from "../../../shared/plans.js";

/** "Falta autorizar no app do banco": how often the page asks whether the bank app approved, and how many times. */
const PENDING_TRIAL_POLL_MS = 5_000;
const PENDING_TRIAL_POLL_MAX = 180;

interface SessionResponse {
  /**
   * "premium_trial": a session the premium trial link opened (PIT-535). The server accepts its token
   * only for /session, /pricing, the trial's /change and /premium-trial/give-up (403 elsewhere), and
   * masks the customer's email, phone and CPF: the page shows the plan and the trial, nothing else,
   * and never displays or prefills those. "full" (or absent, an older server): the billing page.
   */
  session_scope?: "full" | "premium_trial" | (string & {});
  customer: {
    email: string;
    phone: string;
    cpf: string;
    app_language: string;
    needs_phone: boolean;
  };
  zenpost_state: {
    is_paying_customer: boolean;
    /** Absent on a premium_trial session. */
    subscription_plan?: string;
    subscription_period?: string;
    payment_method?: string;
    date_payments_ended?: string | null;
  };
  local_subscription: {
    plan: Plan;
    interval: BillingInterval;
    paymentMethod: PaymentMethod;
    status: string;
    currentPeriodEnd?: string;
    periodAmountCents: number;
  } | null;
  can_raise_in_place?: boolean;
  /** What "Manter Premium" charges from the next renewal: Premium plus the add-ons it keeps. */
  raise_in_place_cents?: number | null;
  scheduled_changes: Array<{
    id: string;
    kind: string;
    effectiveAt: string;
    payload?: Record<string, unknown>;
    provider_swap_status?: string | null;
    pending_order_id?: string | null;
  }>;
  pending_schedule_setup: SessionResponse["scheduled_changes"][number] | null;
  pending_checkout: {
    order_id: string;
    plan: Plan;
    interval: BillingInterval;
    payment_method: PaymentMethod;
    final_cents: number;
    first_charge_at: string | null;
    pix_emv: string | null;
    pix_expires_at: string | null;
  } | null;
  /** Scheduled changes applied during this session load (effective date had passed). */
  just_applied_scheduled_changes?: SessionResponse["scheduled_changes"];
  /** Most recent scheduled change already applied (for ongoing notice). */
  last_applied_scheduled_change?: SessionResponse["scheduled_changes"][number] | null;
  payments: Array<{ id: string; amountCents: number; paidAt: string; description: string; status: string }>;
  /** Inside the 7-day window: the money cancelling can return, and until when. Absent before PIT-380. */
  cancellation_refund?: CancellationRefundOffer | null;
  /** PIX with an open cancellation: keeping it through a new authorization (PIT-461). */
  pix_keep?: PixKeepState | { enabled: false } | null;
  /** "Atualizar cartão" (PIT-459): a live Stripe or Stone card subscription; absent when off. */
  card_update?: { provider: "stripe" | "stone"; past_due: boolean } | null;
  /**
   * A subscription that is over (PIT-546 g): whether its provider can still charge it. false = it said
   * the recurrence is over; null = it did not answer. Absent or null when the subscription is not over.
   */
  expired_billing?: {
    provider_still_billing: boolean | null;
    /** Already asked to cancel, and the provider could not be told: support was. */
    cancel_recorded?: boolean;
  } | null;
  /** Monthly to annual 12x on Stone: the key to tokenize the card with; null when not offered. */
  stone_change?: { public_key: string; installments: number } | null;
  /** A switch to annual 12x still waiting for Stone (its order id); null otherwise. */
  stone_change_pending?: string | null;
  /** "30 dias de Premium sem pagar a mais" (PIT-535): the offer, the trial running, or null. */
  premium_trial?: PremiumTrialPageState | null;
}

/** /pricing for the annual 12x: the first year (less the credit) and each renewal, split in 12. */
interface StoneInstallments {
  count: number;
  first_year_cents: number;
  first_year_installment_cents: number;
  renewal_cents: number;
  renewal_installment_cents: number;
}

interface CancellationRefundOffer {
  amount_cents: number;
  /** The paid charge's currency; absent from an older server, then the language decides. */
  currency?: string;
  refundable_until: string;
}

/** A refund amount in the currency it was paid in, so a language change never relabels it. */
function formatRefundMoney(cents: number, offer: CancellationRefundOffer | null | undefined, locale: string) {
  const currency = offer?.currency === "BRL" || offer?.currency === "USD" ? offer.currency : undefined;
  return formatMoney(cents, locale, currency);
}

export function IntegratedPage() {
  const { t, i18n } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  // The premium trial link (PIT-535) carries `offer` instead of a billing token: the page trades it
  // for a 2 h session limited to the trial (POST /api/integrated/offer-session), and again when that
  // session expires.
  const [offerToken] = useState(() => readPremiumTrialOfferToken(window.location.search));
  const [token, setToken] = useState(() => params.get("token") ?? "");
  /** The token every request uses: an offer session is renewed in place when it expires. */
  const tokenRef = useRef(token);
  const appLanguage = params.get("app_language") ?? undefined;
  const urlTargetPlan = params.get("target_plan");
  const urlTargetInterval = params.get("target_interval");
  const currency = currencyFromLocale(appLanguage ?? i18n.language);
  const money = (cents: number) => formatMoney(cents, i18n.language, currency);
  /** The premium trial is always charged in BRL, whatever language the app's modal runs in. */
  const trialMoney = (cents: number) => formatMoney(cents, i18n.language, "BRL");

  const [session, setSession] = useState<SessionResponse | null>(null);
  /**
   * The page the premium trial link opens (PIT-535): the plan summary and the trial only. Before the
   * session loads, an offer link is one; after, the server says (`session_scope`).
   */
  // Any scope but "full" (a scope this page does not know included): the trial-only page, never the
  // full one. An older server sends no scope: the full page.
  const trialOnly = session ? (session.session_scope ?? "full") !== "full" : !!offerToken;
  const stoneChangeKey = session?.stone_change?.public_key ?? null;
  const [loadErr, setLoadErr] = useState<IntegratedLoadError | null>(null);

  const [plan, setPlan] = useState<Plan>("premium");
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("annual");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("stripe");
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponDraft, setCouponDraft] = useState("");
  const [couponApplied, setCouponApplied] = useState("");
  const [couponError, setCouponError] = useState<string | null>(null);
  const [pricing, setPricing] = useState<PricingBreakdown | null>(null);
  const [changePreview, setChangePreview] = useState<ChangePreview | null>(null);
  const [prorationCents, setProrationCents] = useState(0);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [pricingCanRaiseInPlace, setPricingCanRaiseInPlace] = useState(false);
  /** What the 1-click Premium charges every month, add-ons included (the server's figure). */
  const [pricingRaiseInPlaceCents, setPricingRaiseInPlaceCents] = useState<number | null>(null);
  const [stoneInstallments, setStoneInstallments] = useState<StoneInstallments | null>(null);
  /** A plan picked from the "Está caro" offer that brings the gift: the change asks for it too. */
  const [giftWithChange, setGiftWithChange] = useState<{ plan: Plan; interval: BillingInterval } | null>(null);
  /**
   * "Testar 30 dias" (PIT-535): the offer token of the trial being taken, kept across a CPF retry (the
   * CPF dialog sends to the trial while it is set) and cleared on every other outcome.
   */
  const premiumTrialOfferRef = useRef<string | null>(null);
  const [premiumTrialBusy, setPremiumTrialBusy] = useState(false);
  const [premiumTrialError, setPremiumTrialError] = useState<string | null>(null);
  const premiumTrialStatus = session?.premium_trial?.status ?? null;
  useEffect(() => {
    if (premiumTrialStatus) clarityEvent(`premium_trial_${premiumTrialStatus}_viewed`);
  }, [premiumTrialStatus]);
  /** The change error is Woovi failing to create the PIX: the panel offers to pay by card. */
  const [pixUnavailable, setPixUnavailable] = useState(false);
  const [stoneCard, setStoneCard] = useState<StoneCardFields>(EMPTY_STONE_CARD);
  const [stoneChangeNotice, setStoneChangeNotice] = useState<
    { status: "paid"; plan: Plan; renewsAt: string | null } | { status: "pending" } | null
  >(null);

  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [cancelDialogIntent, setCancelDialogIntent] = useState<"flow" | "reschedule">("flow");
  const [supportBooking, setSupportBooking] = useState<{
    id: string;
    starts_at: string;
    ends_at: string;
  } | null>(null);
  const [supportBookingBusy, setSupportBookingBusy] = useState(false);
  const [supportBookingError, setSupportBookingError] = useState<string | null>(null);
  const [confirmCancelBookingOpen, setConfirmCancelBookingOpen] = useState(false);
  const [changeSubmitting, setChangeSubmitting] = useState(false);
  const [openingPayment, setOpeningPayment] = useState(false);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [fillPhone, setFillPhone] = useState("");
  const [fillCpf, setFillCpf] = useState("");
  const [cpfDialogOpen, setCpfDialogOpen] = useState(false);
  const [cpfDialogError, setCpfDialogError] = useState<string | null>(null);
  const [alreadyScheduledDialog, setAlreadyScheduledDialog] = useState<
    SessionResponse["scheduled_changes"][number] | null
  >(null);
  const [scheduledChangeSupportDialog, setScheduledChangeSupportDialog] = useState<{
    change: SessionResponse["scheduled_changes"][number];
    reason: "cancel" | "replace" | "reverse_openpix_cancellation";
    requestedChangeDetails?: string;
  } | null>(null);
  // The offer as shown when the dialog opened: reloading the session after the refund (or after the
  // window closed) clears session.cancellation_refund, and the dialog must stay to say so.
  const [lateRefundOffer, setLateRefundOffer] = useState<CancellationRefundOffer | null>(null);
  const [reverseCancellationDialog, setReverseCancellationDialog] = useState<
    SessionResponse["scheduled_changes"][number] | null
  >(null);
  const [keepPixDialog, setKeepPixDialog] = useState<
    SessionResponse["scheduled_changes"][number] | null
  >(null);
  const [cardUpdateOpen, setCardUpdateOpen] = useState(false);
  const [keepPremiumDialog, setKeepPremiumDialog] = useState<
    SessionResponse["scheduled_changes"][number] | null
  >(null);
  const [scheduledChangesHighlight, setScheduledChangesHighlight] = useState<"added" | null>(null);
  const [inPlaceUpgradeNotice, setInPlaceUpgradeNotice] = useState<{
    plan: Plan;
    interval: BillingInterval;
    firstChargeAt: string;
    recurringCents: number;
  } | null>(null);
  const inPlaceUpgradeNoticeRef = useRef<HTMLDivElement>(null);
  const [activePixBannerDismissed, setActivePixBannerDismissed] = useState(false);
  const [inlinePix, setInlinePix] = useState<{
    orderId: string;
    emv?: string | null;
    plan: Plan;
    interval: BillingInterval;
    /** "Manter assinatura": approving the code undoes the cancellation, it pays nothing. */
    keep?: boolean;
    /** The code only authorizes Pix Automático, nothing is charged today (from the /change answer). */
    authorizeOnly?: boolean;
    /** "Testar 30 dias" on PIX (PIT-535): always the subscription's own method, never a card switch. */
    premiumTrial?: boolean;
  } | null>(null);
  const [inlineStripe, setInlineStripe] = useState<{
    orderId: string;
    plan: Plan;
    interval: BillingInterval;
    clientSecret?: string | null;
    publishableKey?: string | null;
    finalCents?: number | null;
  } | null>(null);
  const scheduledChangesRef = useRef<HTMLDivElement>(null);
  // The plan picker, where a plan picked from the cancel screen's cheaper-plan card lands (PIT-452).
  const planPickerRef = useRef<HTMLElement>(null);

  const SCHEDULED_HIGHLIGHT_MS = 2400;

  const failLoad = useCallback((loadError: IntegratedLoadError) => {
    clarityEvent(integratedLoadErrorEventName(loadError));
    setLoadErr(loadError);
  }, []);

  // One trade at a time: a focus reload and a tap can both meet the expired session at once.
  const offerExchangeRef = useRef<Promise<string> | null>(null);
  /** The offer link's token for a fresh 2 h session limited to the trial (PIT-535). */
  const exchangeOfferForSession = useCallback((): Promise<string> => {
    if (!offerExchangeRef.current) {
      offerExchangeRef.current = (async () => {
        try {
          const r = await api<{ token: string; scope?: string }>("/api/integrated/offer-session", {
            method: "POST",
            body: JSON.stringify({ offer: offerToken }),
          });
          tokenRef.current = r.token;
          setToken(r.token);
          return r.token;
        } finally {
          offerExchangeRef.current = null;
        }
      })();
    }
    return offerExchangeRef.current;
  }, [offerToken]);

  /**
   * Runs an /api/integrated request with the current token. On the offer link, a 401 (the 2 h
   * session ended) trades the same offer for a new session and runs it once more; when the offer
   * cannot open one any more (expired, gone), the page says so instead of leaving buttons that fail.
   */
  const withSession = useCallback(
    <T,>(run: (sessionToken: string) => Promise<T>): Promise<T> =>
      withOfferSessionRetry({
        offerToken,
        currentToken: () => tokenRef.current,
        refresh: () =>
          exchangeOfferForSession().catch((err: unknown) => {
            logApiError("integrated.offer_session_renew", err);
            const loadError = classifyIntegratedLoadError(err);
            if (loadError.kind !== "temporary") failLoad(loadError);
            throw err;
          }),
        run,
      }),
    [offerToken, exchangeOfferForSession, failLoad],
  );

  const reloadSession = useCallback(async () => {
    if (!tokenRef.current) return null;
    const r = await withSession((sessionToken) =>
      api<SessionResponse>("/api/integrated/session", {
        method: "POST",
        body: JSON.stringify({ token: sessionToken, app_language: appLanguage }),
      }),
    );
    setSession(r);
    if (r.local_subscription) {
      const sub = r.local_subscription;
      setPlan(sub.plan);
      setBillingInterval(sub.interval);
      setPaymentMethod(selectableMethod(sub.paymentMethod));
    }
    if (urlTargetPlan === "premium" || urlTargetPlan === "basic") {
      setPlan(urlTargetPlan);
    }
    if (urlTargetInterval === "monthly" || urlTargetInterval === "annual") {
      setBillingInterval(urlTargetInterval);
    }
    return r;
  }, [withSession, appLanguage, urlTargetPlan, urlTargetInterval]);

  // Once per page: the token in the URL, or the premium trial link's offer traded for a session.
  const bootStartedRef = useRef(false);
  useEffect(() => {
    if (bootStartedRef.current) return;
    bootStartedRef.current = true;
    void (async () => {
      if (!tokenRef.current && offerToken) {
        // The premium trial link: a session while the offer can be taken or the trial runs.
        try {
          await exchangeOfferForSession();
          clarityEvent("premium_trial_link_opened");
        } catch (err) {
          logApiError("integrated.offer_session", err);
          failLoad(classifyIntegratedLoadError(err));
          return;
        }
        // `offer` stays in the address: a reload, a copied URL or "open in the browser" from an app's
        // webview must open the same page, not "ask for a new link". The session it opens is limited
        // to the trial, so the address is worth no more than the WhatsApp message that carries it.
      }
      if (!tokenRef.current) {
        failLoad(MISSING_TOKEN_LOAD_ERROR);
        return;
      }
      try {
        await reloadSession();
      } catch (err) {
        logApiError("integrated.session", err);
        failLoad(classifyIntegratedLoadError(err));
      }
    })();
  }, [offerToken, exchangeOfferForSession, failLoad, reloadSession]);

  // A failed reload keeps the page as it was (an expired offer session is renewed in withSession, and
  // one that cannot be renewed shows the load error there).
  const syncSessionAfterPixFlow = useCallback(() => {
    void reloadSession().catch((err) => logApiError("integrated.session_sync", err));
  }, [reloadSession]);

  // The trial's PIX approved in the bank app while the page showed "Falta autorizar" (not the QR,
  // whose onPaid tells the app): the reload is the only place the app's modal can learn of Premium.
  const premiumTrialStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const status = session?.premium_trial?.status ?? null;
    const previous = premiumTrialStatusRef.current;
    premiumTrialStatusRef.current = status;
    if (previous !== "pending_authorization" || status !== "active") return;
    const sub = session?.local_subscription;
    notifyParentBillingUpdated({
      subscription_plan: sub?.plan ?? "premium",
      subscription_period: sub?.interval ?? "monthly",
      payment_method: sub?.paymentMethod ?? "openpix",
    });
  }, [session?.premium_trial?.status, session?.local_subscription]);

  // After PIX popup closes or user returns to this tab, pick up pending_checkout.
  useEffect(() => {
    if (!token) return;
    const shouldSync = () =>
      !!getStoredActivePixOrderId() ||
      !!session?.pending_checkout ||
      !!session?.pending_schedule_setup ||
      session?.premium_trial?.status === "pending_authorization";

    const onReturn = () => {
      if (shouldSync()) syncSessionAfterPixFlow();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") onReturn();
    };

    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [
    token,
    session?.pending_checkout,
    session?.pending_schedule_setup,
    session?.premium_trial?.status,
    syncSessionAfterPixFlow,
  ]);

  // "Falta autorizar no app do banco": a light poll of the order (no session reload, no Zenpost
  // call) while the page is visible, so the approval shows without a tab switch; the session is
  // reloaded once the order leaves pending. Focus and visibility still reload as above.
  const pendingTrialOrderId =
    session?.premium_trial?.status === "pending_authorization" ? session.premium_trial.order_id : null;
  useEffect(() => {
    if (!pendingTrialOrderId) return;
    let stopped = false;
    let polls = 0;
    const timer = window.setInterval(() => {
      if (stopped || document.visibilityState !== "visible") return;
      polls += 1;
      // Up to 15 minutes; after that the focus reload above is enough.
      if (polls > PENDING_TRIAL_POLL_MAX) {
        window.clearInterval(timer);
        return;
      }
      void api<{ status: string }>(`/api/public/orders/${pendingTrialOrderId}`)
        .then((order) => {
          if (!stopped && order.status !== "pending") syncSessionAfterPixFlow();
        })
        .catch((err) => logApiError("integrated.premium_trial_poll", err));
    }, PENDING_TRIAL_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [pendingTrialOrderId, syncSessionAfterPixFlow]);

  useEffect(() => {
    // The trial-only page has no plan picker to price (PIT-535).
    if (!token || trialOnly) return;
    setPricingLoading(true);
    setPricingError(null);
    void (async () => {
      try {
        const r = await api<{
          breakdown: PricingBreakdown;
          proration_cents: number;
          change_preview: ChangePreview | null;
          coupon_error: string | null;
          can_raise_in_place?: boolean;
          raise_in_place_cents?: number | null;
          stone_installments?: StoneInstallments | null;
        }>(
          "/api/integrated/pricing",
          {
            method: "POST",
            body: JSON.stringify({
              token,
              target_plan: plan,
              target_interval: billingInterval,
              // The card on annual is the 12x on Stone when the session offers it.
              target_payment_method:
                stoneChangeKey && billingInterval === "annual" && paymentMethod === "stripe" ? "stone" : paymentMethod,
              coupon_code: couponApplied || undefined,
              app_language: appLanguage,
            }),
          },
        );
        setPricing(r.breakdown);
        setChangePreview(r.change_preview);
        setProrationCents(r.proration_cents ?? 0);
        setPricingCanRaiseInPlace(Boolean(r.can_raise_in_place));
        setPricingRaiseInPlaceCents(r.raise_in_place_cents ?? null);
        setStoneInstallments(r.stone_installments ?? null);
        if (r.coupon_error === "not_found" && couponApplied) {
          setCouponError("not_found");
          setCouponApplied("");
        }
      } catch (err) {
        // 403 contact_support: this subscription's changes go through support (Stone 12x).
        setPricingError(err instanceof ApiError && err.status === 403 ? "contact_support" : "pricing_failed");
        setPricingCanRaiseInPlace(false);
        setPricingRaiseInPlaceCents(null);
        setStoneInstallments(null);
      } finally {
        setPricingLoading(false);
      }
    })();
  }, [token, trialOnly, plan, billingInterval, paymentMethod, couponApplied, appLanguage, stoneChangeKey]);

  useEffect(() => {
    // The trial-only session is refused by /cancel-rescue (403) and has no cancel flow.
    if (!token || !session || trialOnly) {
      setSupportBooking(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const ctx = await api<{
          active_support_booking: { id: string; starts_at: string; ends_at: string } | null;
        }>(`/api/integrated/cancel-rescue?token=${encodeURIComponent(token)}`);
        if (!cancelled) setSupportBooking(ctx.active_support_booking);
      } catch {
        if (!cancelled) setSupportBooking(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, session, trialOnly, confirmCancelOpen]);

  useEffect(() => {
    if (!scheduledChangesHighlight) return;
    const scrollTimer = window.setTimeout(() => {
      scheduledChangesRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    const clearTimer =
      scheduledChangesHighlight === "added"
        ? window.setTimeout(() => setScheduledChangesHighlight(null), 3500)
        : undefined;
    return () => {
      window.clearTimeout(scrollTimer);
      if (clearTimer) window.clearTimeout(clearTimer);
    };
  }, [scheduledChangesHighlight]);

  useEffect(() => {
    if (!inPlaceUpgradeNotice) return;
    const scrollTimer = window.setTimeout(() => {
      inPlaceUpgradeNoticeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    return () => window.clearTimeout(scrollTimer);
  }, [inPlaceUpgradeNotice]);

  const current = session?.local_subscription ?? null;
  const planPriceDeltas = useMemo(() => {
    if (!current || billingInterval !== current.interval) return {};
    const currentCents = getPlanPricing(current.plan, billingInterval, currency).totalCents;
    const deltas: Partial<Record<Plan, number>> = {};
    for (const p of Object.keys(PLANS) as Plan[]) {
      if (p !== current.plan) {
        deltas[p] = getPlanPricing(p, billingInterval, currency).totalCents - currentCents;
      }
    }
    return deltas;
  }, [current, billingInterval, currency]);
  const subscriptionExpired = subscriptionEnded({
    status: current?.status,
    currentPeriodEnd: current?.currentPeriodEnd,
    isPayingCustomer: session?.zenpost_state.is_paying_customer,
    datePaymentsEnded: session?.zenpost_state.date_payments_ended,
  });
  // Over, and the provider said it charges nothing more: there is nothing to cancel (PIT-546 g).
  const expiredNothingToCancel = subscriptionExpired && session?.expired_billing?.provider_still_billing === false;
  const expiredCancelRecorded = subscriptionExpired && !!session?.expired_billing?.cancel_recorded;

  const alreadyFullyRefunded =
    !!session &&
    session.payments.length > 0 &&
    session.payments.every((p) => p.status !== "paid");

  const showCancelPlan =
    !!current &&
    !alreadyFullyRefunded &&
    current.status !== "canceled" &&
    !expiredNothingToCancel &&
    !expiredCancelRecorded;
  // The subscription as the cancel dialog reads it, over or not; the cheaper plan prices only a live one.
  const cancelDialogCurrent: CheaperPlanCurrent | null = current
    ? {
        plan: current.plan,
        interval: current.interval,
        paymentMethod: current.paymentMethod,
        hasScheduledChange: (session?.scheduled_changes.length ?? 0) > 0,
      }
    : null;
  // Where "Quero trocar de plano" leads in the cancel dialog (PIT-546 f): over or not, never the plain
  // confirmation. The row status and a pending switch to the 12x say whether this page can change it.
  const cancelPlanChange = planChangePath(cancelDialogCurrent, {
    subscriptionOver: subscriptionExpired,
    status: current?.status,
    stoneChangePending: !!session?.stone_change_pending,
  });

  const justAppliedScheduledChanges = session?.just_applied_scheduled_changes ?? [];
  const appliedScheduledChangeNotice = useMemo(() => {
    if (justAppliedScheduledChanges.length > 0) return justAppliedScheduledChanges;
    if ((session?.scheduled_changes.length ?? 0) > 0) return [];
    const last = session?.last_applied_scheduled_change;
    if (!last || !current) return [];
    return localSubscriptionMatchesAppliedChange(last, current) ? [last] : [];
  }, [justAppliedScheduledChanges, session?.scheduled_changes.length, session?.last_applied_scheduled_change, current]);
  const justAppliedCancellation = appliedScheduledChangeNotice.some((s) => s.kind === "cancellation");
  const showSubscriptionExpiredBanner = subscriptionExpired && !justAppliedCancellation;
  const customerPhone = session?.customer.phone || fillPhone;
  const pixLocaleAvailable = isPixAvailableForLocale(appLanguage ?? i18n.language);
  const pixAvailable =
    pixLocaleAvailable && isPixAvailableForPhone(customerPhone, "BR");

  useEffect(() => {
    // The trial-only session masks the phone, so it never reads as Brazilian: no picker to correct.
    if (trialOnly) return;
    if (!pixAvailable && paymentMethod === "openpix") {
      setPaymentMethod("stripe");
    }
  }, [trialOnly, pixAvailable, paymentMethod]);

  const selectionUnchanged =
    !!current &&
    plan === current.plan &&
    billingInterval === current.interval &&
    paymentMethod === current.paymentMethod;
  const selectionBlocksChange = selectionUnchanged && !subscriptionExpired;

  const previewMatches = previewMatchesSelection(changePreview, plan, billingInterval);

  const isScheduledChange =
    previewMatches && !!changePreview && isScheduledDecision(changePreview.decision);

  const pendingScheduleBlocked =
    previewMatches && changePreview?.decision === "blocked_pending_scheduled_change";

  const annualToMonthlyPixBlocked =
    previewMatches && changePreview?.decision === "blocked_annual_to_monthly_pix";

  const pendingScheduledChange = session?.scheduled_changes[0] ?? null;
  const pendingScheduleSetup = session?.pending_schedule_setup ?? null;
  /** The sticky confirm finishes the unfinished setup only while it is what is selected; another choice goes to /change, which voids it. */
  const resumesPendingSetup =
    !!pendingScheduleSetup &&
    ((selectionUnchanged && !subscriptionExpired) ||
      !!findMatchingScheduledChange([pendingScheduleSetup], { plan, interval: billingInterval, paymentMethod }));
  /** Locked UI + "Cancelar alteração" only after provider approval — not while authorization is pending. */
  const approvedScheduledChange = pendingScheduledChange;
  /**
   * PIT-535: while the trial runs the server takes no other change (409 premium_trial_running), so the
   * picker would only price days nobody paid and fail at confirm.
   */
  const premiumTrialLocksChanges = session?.premium_trial?.status === "active";
  const changesLocked = !!approvedScheduledChange || premiumTrialLocksChanges;

  useEffect(() => {
    if (!changesLocked || !current) return;
    setPlan(current.plan);
    setBillingInterval(current.interval);
    setPaymentMethod(selectableMethod(current.paymentMethod));
  }, [changesLocked, current?.plan, current?.interval, current?.paymentMethod]);

  const activePendingPix = useMemo(() => {
    const pending = session?.pending_checkout;
    if (!pending) return null;
    if (
      !pendingCheckoutMatchesSelection({
        pending,
        plan,
        interval: billingInterval,
        paymentMethod,
      })
    ) {
      return null;
    }
    const snapshot: PixOrderSnapshot = {
      id: pending.order_id,
      status: "pending",
      payment_method: "openpix",
      pix_expires_at: pending.pix_expires_at,
    };
    return isPixOrderActive(snapshot) ? pending : null;
  }, [session?.pending_checkout, plan, billingInterval, paymentMethod]);

  useEffect(() => {
    setActivePixBannerDismissed(false);
  }, [session?.pending_checkout?.order_id]);

  const showActivePendingPix = activePendingPix && !activePixBannerDismissed;

  const dueTodayCents = useMemo(
    () =>
      computeIntegratedDueTodayCents({
        selectionUnchanged,
        subscriptionExpired,
        canRaiseInPlace: pricingCanRaiseInPlace,
        preview: changePreview,
        pricing,
        plan,
        interval: billingInterval,
      }),
    [selectionUnchanged, subscriptionExpired, pricingCanRaiseInPlace, changePreview, pricing, plan, billingInterval],
  );

  const showCompleteData = session?.customer.needs_phone;

  // The card on annual is the 12x on Stone for a monthly subscriber (session `stone_change`): the
  // installment is the number that leads, the first year's total sits in the panel.
  const payWithStone =
    Boolean(stoneChangeKey) &&
    billingInterval === "annual" &&
    paymentMethod === "stripe" &&
    stoneInstallments != null &&
    changePreview?.decision === "immediate_upgrade" &&
    changePreview.targetPlan === plan &&
    changePreview.targetInterval === "annual";
  const stoneInstallmentLabel = payWithStone
    ? t("integrated.stoneChangeInstallment", {
        count: stoneInstallments!.count,
        installment: money(stoneInstallments!.first_year_installment_cents),
      })
    : "";

  const dueTodayLabel = payWithStone
    ? stoneInstallmentLabel
    : dueTodayCents == null
      ? (pricingLoading ? t("common.loading") : "—")
      : money(dueTodayCents);

  function describeFormRequestedChange(): string {
    if (!current) return "";
    return describeRequestedChange(
      {
        current: {
          plan: current.plan,
          interval: current.interval,
          paymentMethod: current.paymentMethod,
          currentPeriodEnd: current.currentPeriodEnd ?? undefined,
        },
        target: { plan, interval: billingInterval, paymentMethod },
        dueTodayLabel,
      },
      t,
      i18n.language,
    );
  }

  function buildSupportWhatsAppUrl(args: {
    intent: BillingSupportIntent;
    scheduledChanges?: SessionResponse["scheduled_changes"];
    change?: SessionResponse["scheduled_changes"][number];
    requestedChangeDetails?: string;
  }): string {
    const email = session?.customer.email ?? "";
    const scheduled =
      args.scheduledChanges ??
      (args.change ? [args.change] : session?.scheduled_changes ?? []);
    const scheduledChangeDetails = scheduled
      .map((s) => describeScheduledChange(s, t, i18n.language))
      .join("\n");
    return buildBillingSupportWhatsAppUrl(t, {
      email,
      intent: args.intent,
      scheduledChangeDetails: scheduledChangeDetails || undefined,
      requestedChangeDetails: args.requestedChangeDetails,
    });
  }

  const scheduledChangeHelpWhatsAppUrl = buildSupportWhatsAppUrl({
    intent: "scheduled_change_help",
  });

  const showsScheduledPricing =
    isScheduledChange || (pendingScheduleBlocked && (changePreview?.chargeNowCents ?? 0) === 0);

  // Premium plus the add-ons the subscription keeps: what the raise charges, not the bare list price.
  const raiseInPlaceAmount = money(
    pricingRaiseInPlaceCents ?? getPlanPricing("premium", "monthly", currency).totalCents,
  );
  const raiseInPlaceHint = pricingCanRaiseInPlace
    ? t("integrated.raiseInPlaceHint", { amount: raiseInPlaceAmount })
    : null;

  const confirmLabel = payWithStone
    ? t("integrated.stoneChangeCta", {
        count: stoneInstallments!.count,
        installment: money(stoneInstallments!.first_year_installment_cents),
      })
    : raiseInPlaceHint
    ? t("integrated.raiseInPlaceCta", { amount: raiseInPlaceAmount })
    : showActivePendingPix
    ? t("checkout.resumePixCta")
    : subscriptionExpired
      ? t("integrated.confirmReactivation")
    : showsScheduledPricing
      ? t("integrated.confirmSchedule")
      : t("integrated.confirmChange");

  // The result of a switch to 12x, or one still waiting for Stone after a reload.
  const stoneNotice =
    stoneChangeNotice ?? (session?.stone_change_pending ? ({ status: "pending" } as const) : null);

  const stonePanel = payWithStone
    ? {
        installments: stoneInstallments!,
        card: stoneCard,
        onCardChange: setStoneCard,
        inRefundWindow: session?.cancellation_refund != null,
      }
    : null;

  // Woovi failed to create the PIX: the same change, by card.
  const payByCard =
    pixUnavailable && paymentMethod === "openpix"
      ? () => {
          setPaymentMethod("stripe");
          setChangeError(null);
          setPixUnavailable(false);
        }
      : undefined;

  async function applyCoupon() {
    setCouponError(null);
    const code = couponDraft.trim().toUpperCase();
    if (!code) return;
    try {
      const r = await api<{
        breakdown: PricingBreakdown;
        proration_cents: number;
        change_preview: ChangePreview | null;
        coupon_error: string | null;
        can_raise_in_place?: boolean;
        raise_in_place_cents?: number | null;
      }>("/api/integrated/pricing", {
        method: "POST",
        body: JSON.stringify({
          token,
          target_plan: plan,
          target_interval: billingInterval,
          target_payment_method: paymentMethod,
          coupon_code: code,
          app_language: appLanguage,
        }),
      });
      if (r.coupon_error === "not_found") {
        setCouponError("not_found");
        return;
      }
      setCouponApplied(code);
      setPricing(r.breakdown);
      setChangePreview(r.change_preview);
      setProrationCents(r.proration_cents ?? 0);
      setPricingCanRaiseInPlace(Boolean(r.can_raise_in_place));
      setPricingRaiseInPlaceCents(r.raise_in_place_cents ?? null);
      setCouponDraft("");
      setCouponOpen(false);
    } catch {
      setCouponError("not_found");
    }
  }

  function removeCoupon() {
    setCouponApplied("");
    setCouponDraft("");
    setCouponError(null);
  }

  function postBillingUpdatedToParent(action?: "cancellation") {
    notifyParentBillingUpdated({
      subscription_plan: plan,
      subscription_period: billingInterval,
      payment_method: paymentMethod,
      ...(action ? { action } : {}),
    });
  }

  // Payment popup (PIX / Stripe) posts here; forward to app.zenpost.ai parent modal.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as {
        type?: string;
        subscription_plan?: Plan;
        subscription_period?: BillingInterval;
        payment_method?: PaymentMethod;
      };
      if (data?.type !== "ZENPOST_BILLING_UPDATED") return;
      void reloadSession()
        .then((r) => {
          if (r?.scheduled_changes.length && !r.pending_schedule_setup) {
            setScheduledChangesHighlight("added");
          }
        })
        .catch(() => {});
      if (!data.subscription_plan || !data.subscription_period) return;
      notifyParentBillingUpdated({
        subscription_plan: data.subscription_plan,
        subscription_period: data.subscription_period,
        payment_method: data.payment_method,
      });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [reloadSession]);

  function addScheduledChangeAndHighlight(change: SessionResponse["scheduled_changes"][number]) {
    setSession((prev) => {
      if (!prev) return prev;
      const scheduled_changes = [change];
      const local_subscription = prev.local_subscription
        ? { ...prev.local_subscription, status: "scheduled_change" }
        : null;
      return { ...prev, scheduled_changes, local_subscription };
    });
    setScheduledChangesHighlight("added");
  }

  function wait(ms: number) {
    return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
  }

  function closeAlreadyScheduledDialog() {
    setAlreadyScheduledDialog(null);
    setScheduledChangesHighlight("added");
    window.setTimeout(() => {
      scheduledChangesRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    window.setTimeout(() => setScheduledChangesHighlight(null), SCHEDULED_HIGHLIGHT_MS);
  }

  type ChangeApiResponse = {
    ok: boolean;
    decision: { kind: string; reason?: string; effectiveAt?: string };
    order?: {
      id: string;
      final_cents?: number;
      proration_cents?: number;
      first_charge_at?: string;
      recurring_cents?: number;
      stripe_client_secret?: string | null;
      stripe_publishable_key?: string | null;
      stripe_in_place_upgrade?: boolean;
      pix_emv?: string;
    };
    scheduled_change_id?: string;
    scheduled_change?: SessionResponse["scheduled_changes"][number];
    raised_in_place?: boolean;
    period_amount_cents?: number;
    /** Monthly to annual 12x on Stone: charged in the request (`paid`) or waiting for Stone. */
    stone?: { status: "paid" | "pending"; order_id: string };
    /** "Testar 30 dias" (PIT-535): the change redeemed the premium trial offer. */
    premium_trial?: boolean;
  };

  async function keepPremiumInPlace(scheduledChangeId: string) {
    setChangeSubmitting(true);
    setChangeError(null);
    try {
      const r = await api<{
        ok: boolean;
        raised_in_place?: boolean;
        period_amount_cents?: number;
      }>("/api/integrated/scheduled-changes/" + scheduledChangeId + "/cancel", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      if (r.raised_in_place && current?.currentPeriodEnd) {
        setInPlaceUpgradeNotice({
          plan: "premium",
          interval: "monthly",
          firstChargeAt: current.currentPeriodEnd,
          recurringCents:
            r.period_amount_cents ?? getPlanPricing("premium", "monthly", currency).totalCents,
        });
      }
      postBillingUpdatedToParent();
      await reloadSession();
      setKeepPremiumDialog(null);
    } catch {
      setChangeError(t("integrated.checkoutStartFailed"));
    } finally {
      setChangeSubmitting(false);
    }
  }

  async function performChangeRequest(overrideCpf?: string, stoneCardToken?: string): Promise<ChangeApiResponse> {
    return api<ChangeApiResponse>("/api/integrated/change", {
      method: "POST",
      body: JSON.stringify({
        token,
        target_plan: plan,
        target_interval: billingInterval,
        target_payment_method: stoneCardToken ? "stone" : paymentMethod,
        card_token: stoneCardToken,
        retention_gift:
          giftWithChange && giftWithChange.plan === plan && giftWithChange.interval === billingInterval
            ? true
            : undefined,
        coupon_code: couponApplied || undefined,
        fill_phone: session?.customer.needs_phone ? fillPhone : undefined,
        fill_cpf: overrideCpf?.replace(/\D/g, ""),
        app_language: appLanguage,
      }),
    });
  }

  function openCpfRetryDialog() {
    setCpfDialogError(null);
    // A trial-only session's CPF is masked ("•••12"): never put it in the field.
    if (!fillCpf && session?.customer.cpf && !trialOnly) {
      setFillCpf(formatCPF(session.customer.cpf));
    }
    setCpfDialogOpen(true);
  }

  function syncCustomerCpf(cpfDigits: string) {
    setSession((prev) =>
      prev
        ? {
            ...prev,
            customer: {
              ...prev.customer,
              cpf: cpfDigits,
            },
          }
        : prev,
    );
  }

  async function openPendingStripeOrder(
    orderId: string,
    targetPlan: Plan,
    targetInterval: BillingInterval,
    opts?: {
      clientSecret?: string | null;
      publishableKey?: string | null;
      finalCents?: number | null;
    },
  ) {
    if (openingPayment) return;
    setOpeningPayment(true);
    setChangeError(null);
    try {
      saveStripeCheckoutContext({
        orderId,
        plan: targetPlan,
        interval: targetInterval,
        email: trialOnly ? "" : (session?.customer.email ?? ""),
        returnTo: "integrated",
        // The trial link's 2 h session is never written down (PIT-535).
        integratedToken: trialOnly ? undefined : token,
      });
      const publishableKey = opts?.publishableKey ?? "";
      if (opts?.clientSecret && publishableKey) {
        saveStripeCheckoutSession(orderId, {
          clientSecret: opts.clientSecret,
          publishableKey,
          finalCents: opts.finalCents ?? 0,
        });
      }
      setInlineStripe({
        orderId,
        plan: targetPlan,
        interval: targetInterval,
        clientSecret: opts?.clientSecret ?? null,
        publishableKey: opts?.publishableKey ?? null,
        finalCents: opts?.finalCents ?? null,
      });
    } catch {
      setChangeError(t("integrated.checkoutStartFailed"));
    } finally {
      setOpeningPayment(false);
    }
  }

  async function openPendingPixOrder(
    orderId: string,
    targetPlan: Plan,
    targetInterval: BillingInterval,
    initialEmv?: string | null,
    authorizeOnly?: boolean,
    opts?: { premiumTrial?: boolean },
  ) {
    if (openingPayment) return;
    setOpeningPayment(true);
    setChangeError(null);
    try {
      savePixCheckoutContext(
        trialOnly
          ? // The trial link's session: masked contact data and a 2 h token are never written down.
            { orderId, plan: targetPlan, interval: targetInterval, email: "", returnTo: "integrated" }
          : {
              orderId,
              plan: targetPlan,
              interval: targetInterval,
              email: session?.customer.email ?? "",
              phone: session?.customer.phone || fillPhone || undefined,
              cpf: (session?.customer.cpf || fillCpf.replace(/\D/g, "")) || undefined,
              returnTo: "integrated",
              integratedToken: token,
            },
      );
      if (initialEmv) sessionStorage.setItem(`pix_emv_${orderId}`, initialEmv);
      setInlinePix({
        orderId,
        emv: initialEmv ?? null,
        plan: targetPlan,
        interval: targetInterval,
        authorizeOnly,
        premiumTrial: opts?.premiumTrial,
      });
    } catch {
      if (opts?.premiumTrial) setPremiumTrialError(t("integrated.premiumTrial.acceptFailed"));
      else setChangeError(t("integrated.checkoutStartFailed"));
    } finally {
      setOpeningPayment(false);
    }
  }

  async function resumeScheduledProviderSetup(change: SessionResponse["scheduled_changes"][number]) {
    const orderId = change.pending_order_id;
    if (!orderId) return;

    const targetPlan = (change.payload?.target_plan as Plan | undefined) ?? plan;
    const targetInterval = (change.payload?.target_interval as BillingInterval | undefined) ?? billingInterval;
    const targetPaymentMethod =
      (change.payload?.target_payment_method as PaymentMethod | undefined) ?? paymentMethod;

    if (targetPaymentMethod === "stripe") {
      await openPendingStripeOrder(orderId, targetPlan, targetInterval);
      return;
    }

    await openPendingPixOrder(orderId, targetPlan, targetInterval);
  }

  function redirectToProviderCheckout(order: {
    id: string;
    final_cents?: number;
    stripe_client_secret?: string;
    stripe_publishable_key?: string | null;
    pix_emv?: string;
    first_charge_at?: string;
  }) {
    if (paymentMethod === "stripe") {
      void openPendingStripeOrder(order.id, plan, billingInterval, {
        clientSecret: order.stripe_client_secret,
        publishableKey: order.stripe_publishable_key,
        finalCents: order.final_cents,
      });
      return;
    }
    if (paymentMethod === "openpix") {
      void openPendingPixOrder(
        order.id,
        plan,
        billingInterval,
        order.pix_emv,
        order.final_cents === 0 && !!order.first_charge_at,
      );
    }
  }

  async function handleChangeResponse(r: ChangeApiResponse) {
    // The change went through: a gift that came with it was asked for with it.
    if (r.ok) setGiftWithChange(null);
    if (r.stone) {
      setStoneCard(EMPTY_STONE_CARD);
      if (r.stone.status === "paid") {
        const reloaded = await reloadSession();
        setStoneChangeNotice({
          status: "paid",
          plan,
          renewsAt: reloaded?.local_subscription?.currentPeriodEnd ?? null,
        });
        postBillingUpdatedToParent();
      } else {
        setStoneChangeNotice({ status: "pending" });
      }
      return;
    }
    if (r.decision?.kind === "no_op") {
      if (r.decision.reason?.includes("already scheduled")) {
        const fallback =
          findMatchingScheduledChange(session?.scheduled_changes ?? [], {
            plan,
            interval: billingInterval,
            paymentMethod,
          }) ?? session?.scheduled_changes[0];
        if (fallback) {
          setAlreadyScheduledDialog(fallback);
          return;
        }
      }
      return;
    }

    if (r.decision?.kind?.startsWith("schedule_")) {
      if (r.order?.id) {
        redirectToProviderCheckout(r.order);
        return;
      }

      const scheduledChange =
        r.scheduled_change ??
        ({
          id: r.scheduled_change_id ?? `scheduled-${Date.now()}`,
          kind:
            r.decision.kind === "schedule_downgrade_plan"
              ? "downgrade_plan"
              : r.decision.kind === "schedule_downgrade_interval"
                ? "downgrade_interval"
                : r.decision.kind === "schedule_payment_method_change"
                  ? "payment_method_change"
                  : "cancellation",
          effectiveAt: r.decision.effectiveAt ?? new Date().toISOString(),
          payload: {
            target_plan: plan,
            target_interval: billingInterval,
            target_payment_method: paymentMethod,
          },
        } as SessionResponse["scheduled_changes"][number]);
      addScheduledChangeAndHighlight(scheduledChange);
      if (current) {
        setPlan(current.plan);
        setBillingInterval(current.interval);
        setPaymentMethod(selectableMethod(current.paymentMethod));
      }
      postBillingUpdatedToParent();
      return;
    }

    if (r.order?.stripe_in_place_upgrade || r.raised_in_place) {
      const firstChargeAt = r.order?.first_charge_at ?? current?.currentPeriodEnd;
      if (firstChargeAt) {
        setInPlaceUpgradeNotice({
          plan,
          interval: billingInterval,
          firstChargeAt,
          recurringCents: r.order?.recurring_cents ?? r.period_amount_cents ?? 0,
        });
      }
      postBillingUpdatedToParent();
      await reloadSession();
      return;
    }

    if (r.order?.id) {
      redirectToProviderCheckout(r.order);
      return;
    }

    if (r.decision?.kind === "immediate_payment_method_change") {
      postBillingUpdatedToParent();
      window.location.reload();
      return;
    }

    setChangeError(t("integrated.checkoutStartFailed"));
  }

  /**
   * After the trial was taken by card or given up: the page reloads first and only then tells the
   * embedding app, with the plan the server now has (the selection state in this closure is the old
   * one). The change already happened, so a reload that fails is no failure: the page shows the
   * result it knows and reloads again shortly.
   */
  async function settlePremiumTrialChange(args: {
    expected: { plan: Plan; interval: BillingInterval; paymentMethod: PaymentMethod };
    optimistic: (s: SessionResponse) => SessionResponse;
  }) {
    let reloaded: SessionResponse | null = null;
    try {
      reloaded = await reloadSession();
    } catch (err) {
      logApiError("integrated.premium_trial_reload", err);
      setSession((s) => (s ? args.optimistic(s) : s));
      retryReloadSoon();
    }
    const sub = reloaded?.local_subscription;
    notifyParentBillingUpdated({
      subscription_plan: sub?.plan ?? args.expected.plan,
      subscription_period: sub?.interval ?? args.expected.interval,
      payment_method: sub?.paymentMethod ?? args.expected.paymentMethod,
    });
  }

  function retryReloadSoon(attempt = 1) {
    window.setTimeout(() => {
      void reloadSession().catch((err) => {
        logApiError("integrated.session_retry", err);
        if (attempt < 3) retryReloadSoon(attempt + 1);
      });
    }, 3000 * attempt);
  }

  /** Reload after a refusal that means the trial changed; a reload error leaves the page as it is. */
  async function reloadAfterPremiumTrialRefusal(): Promise<SessionResponse | null> {
    try {
      return await reloadSession();
    } catch (err) {
      logApiError("integrated.premium_trial_reload", err);
      return null;
    }
  }

  /** PIX needs a phone Woovi accepts; the page asks for one when the stored one is not (needs_phone). */
  const premiumTrialNeedsPhone =
    !!session?.customer.needs_phone && current?.paymentMethod === "openpix" && session?.premium_trial?.status === "available";

  function startPremiumTrial() {
    const trial = session?.premium_trial;
    if (trial?.status !== "available" || !current) return;
    // The same rule the server and Woovi apply (12 digits with 55), so a short phone is caught here.
    if (premiumTrialNeedsPhone && (!isValidPhoneE164(fillPhone) || phoneUnusableForPix(fillPhone))) {
      setPremiumTrialError(t("integrated.premiumTrial.phoneNeeded"));
      return;
    }
    clarityEvent("premium_trial_accept_clicked");
    premiumTrialOfferRef.current = trial.offer_token;
    void acceptPremiumTrial();
  }

  /**
   * "Testar 30 dias" (PIT-535): /change with the offer. The server always uses the subscription's own
   * method (card in place, or a PIX authorization), so the plan picker is not read, and every outcome
   * lands on the trial panel, never on the plan change one.
   */
  async function acceptPremiumTrial(overrideCpf?: string) {
    const offer = premiumTrialOfferRef.current;
    const trial = session?.premium_trial;
    const sub = current;
    if (!offer || !sub || premiumTrialBusy) return;
    const method = selectableMethod(sub.paymentMethod);
    setPremiumTrialBusy(true);
    setPremiumTrialError(null);
    try {
      const r = await withSession((sessionToken) =>
        api<ChangeApiResponse>("/api/integrated/change", {
          method: "POST",
          body: JSON.stringify({
            token: sessionToken,
            target_plan: "premium",
            target_interval: "monthly",
            target_payment_method: method,
            premium_trial_offer: offer,
            fill_phone: session?.customer.needs_phone && fillPhone ? fillPhone : undefined,
            fill_cpf: overrideCpf?.replace(/\D/g, ""),
            app_language: appLanguage,
          }),
        }),
      );
      premiumTrialOfferRef.current = null;
      setCpfDialogOpen(false);
      if (r.order?.stripe_in_place_upgrade) {
        // Premium already, on the card on file: the trial panel shows the first charge's date and amount.
        clarityEvent("premium_trial_card_accepted");
        await settlePremiumTrialChange({
          expected: { plan: "premium", interval: "monthly", paymentMethod: method },
          optimistic: (s) => ({
            ...s,
            local_subscription: s.local_subscription
              ? {
                  ...s.local_subscription,
                  plan: "premium",
                  interval: "monthly",
                  periodAmountCents:
                    r.order?.recurring_cents ??
                    (trial?.status === "available" ? trial.premium_cents : s.local_subscription.periodAmountCents),
                }
              : null,
            premium_trial:
              trial?.status === "available"
                ? {
                    status: "active",
                    first_charge_at: r.order?.first_charge_at ?? trial.first_charge_at,
                    premium_cents: r.order?.recurring_cents ?? trial.premium_cents,
                    basic_cents: trial.basic_cents,
                    can_give_up: true,
                    give_up_until: premiumTrialGiveUpClosesAt(
                      new Date(r.order?.first_charge_at ?? trial.first_charge_at),
                      "stripe",
                    ).toISOString(),
                  }
                : s.premium_trial,
          }),
        });
        return;
      }
      if (r.order?.id && method === "openpix") {
        // The authorization in the bank app; nothing is charged today.
        clarityEvent("premium_trial_pix_requested");
        await openPendingPixOrder(r.order.id, "premium", "monthly", r.order.pix_emv, true, { premiumTrial: true });
        return;
      }
      // The card only changes in place (the server refuses instead of a new card checkout).
      setPremiumTrialError(t("integrated.premiumTrial.acceptFailed"));
    } catch (err) {
      logApiError("integrated.premium_trial_accept", err);
      const code = err instanceof ApiError ? apiErrorCode(err) : undefined;
      if (code === "invalid_cpf") {
        // The only error that keeps the offer: the CPF dialog sends it again with the CPF.
        if (overrideCpf) {
          setCpfDialogError(t("checkout.errors.cpf"));
          setCpfDialogOpen(true);
        } else {
          openCpfRetryDialog();
        }
        return;
      }
      premiumTrialOfferRef.current = null;
      setCpfDialogOpen(false);
      if (code === "offer_expired") {
        await reloadAfterPremiumTrialRefusal();
        setPremiumTrialError(t("integrated.premiumTrial.offerExpired"));
        return;
      }
      if (code === "offer_not_found" || code === "not_eligible") {
        // Used, gone, or the account no longer qualifies: show what is left. Taken in another tab or
        // device, the reloaded panel already shows the trial, so no "not available" next to it.
        const reloaded = await reloadAfterPremiumTrialRefusal();
        const status = reloaded?.premium_trial?.status;
        if (status !== "active" && status !== "pending_authorization") {
          setPremiumTrialError(t("integrated.premiumTrial.unavailable"));
        }
        return;
      }
      if (isPixUnavailableError(err)) {
        // No card switch here: the trial always runs on the subscription's own method.
        setPremiumTrialError(t("integrated.pixUnavailableRetry"));
        return;
      }
      if (code === "invalid_phone") {
        setPremiumTrialError(t("integrated.premiumTrial.phoneNeeded"));
        return;
      }
      // 503 try_again (premium_trial_try_again): Stripe did not answer about the card; acceptFailed
      // already says to try again in a few minutes.
      setPremiumTrialError(t("integrated.premiumTrial.acceptFailed"));
    } finally {
      setPremiumTrialBusy(false);
    }
  }

  /** pending_authorization: the same PIX authorization the tap made, never a second one. */
  function resumePremiumTrialPix() {
    const trial = session?.premium_trial;
    if (trial?.status !== "pending_authorization") return;
    setPremiumTrialError(null);
    const pending = session?.pending_checkout?.order_id === trial.order_id ? session.pending_checkout : null;
    void openPendingPixOrder(trial.order_id, "premium", "monthly", pending?.pix_emv ?? null, true, {
      premiumTrial: true,
    });
  }

  async function giveUpPremiumTrial() {
    const trial = session?.premium_trial;
    const sub = current;
    if (premiumTrialBusy || !sub) return;
    setPremiumTrialBusy(true);
    setPremiumTrialError(null);
    try {
      let done: { ok: boolean; period_amount_cents?: number; first_charge_at?: string };
      try {
        done = await withSession((sessionToken) =>
          api<{ ok: boolean; period_amount_cents?: number; first_charge_at?: string }>(
            "/api/integrated/premium-trial/give-up",
            { method: "POST", body: JSON.stringify({ token: sessionToken }) },
          ),
        );
      } catch (err) {
        logApiError("integrated.premium_trial_give_up", err);
        const code = err instanceof ApiError ? apiErrorCode(err) : undefined;
        if (code === "premium_trial_give_up_closed") {
          // Past give_up_until: the reloaded panel drops the button and says why. Said here only if
          // the panel would not (a failed reload, a device clock behind the server's).
          const reloaded = await reloadAfterPremiumTrialRefusal();
          if (!reloaded || premiumTrialGiveUpOpen(reloaded.premium_trial, Date.now())) {
            setPremiumTrialError(t("integrated.premiumTrial.giveUpClosed"));
          }
          return;
        }
        if (code === "premium_trial_not_running" || code === "no_premium_trial") {
          // The trial changed since the page loaded (another tab, the first charge, support): show it
          // as it is now instead of "try again", which would fail the same way.
          const reloaded = await reloadAfterPremiumTrialRefusal();
          if (reloaded?.premium_trial?.status !== "given_up") {
            setPremiumTrialError(t("integrated.premiumTrial.giveUpStateChanged"));
          }
          return;
        }
        // give_up_failed (the provider refused; nothing changed), a server or network error.
        setPremiumTrialError(t("integrated.premiumTrial.giveUpFailed"));
        return;
      }
      clarityEvent("premium_trial_give_up");
      const basicCents = done.period_amount_cents ?? (trial && "basic_cents" in trial ? trial.basic_cents : undefined);
      await settlePremiumTrialChange({
        expected: { plan: "basic", interval: "monthly", paymentMethod: sub.paymentMethod },
        optimistic: (s) => ({
          ...s,
          local_subscription: s.local_subscription
            ? {
                ...s.local_subscription,
                plan: "basic",
                periodAmountCents: basicCents ?? s.local_subscription.periodAmountCents,
              }
            : null,
          premium_trial: {
            status: "given_up",
            first_charge_at: done.first_charge_at ?? trial?.first_charge_at ?? new Date().toISOString(),
            basic_cents: basicCents ?? s.local_subscription?.periodAmountCents ?? 0,
          },
        }),
      });
    } finally {
      setPremiumTrialBusy(false);
    }
  }

  async function executeChange(overrideCpf?: string) {
    setChangeSubmitting(true);
    setChangeError(null);
    setPixUnavailable(false);
    try {
      let stoneCardToken: string | undefined;
      if (payWithStone && stoneChangeKey) {
        const invalid = invalidStoneCardField(stoneCard);
        if (invalid) {
          setChangeError(t(`checkout.stone.errors.${invalid}`));
          return;
        }
        try {
          // Tokenized again on every attempt (a CPF retry included): the card never reaches zenpay.
          stoneCardToken = await tokenizeStoneCard(stoneChangeKey, stoneCard);
        } catch {
          setChangeError(t("checkout.stone.errors.tokenize"));
          return;
        }
      }
      const r = await performChangeRequest(overrideCpf, stoneCardToken);
      if (overrideCpf) {
        syncCustomerCpf(overrideCpf.replace(/\D/g, ""));
      }
      setCpfDialogOpen(false);
      await handleChangeResponse(r);
    } catch (err) {
      // /change voids an unfinished setup for another target before it can fail: stop
      // offering it. Only that field: reloading the session would reset the selection the
      // CPF retry is about to send.
      if (
        pendingScheduleSetup &&
        !findMatchingScheduledChange([pendingScheduleSetup], { plan, interval: billingInterval, paymentMethod })
      ) {
        setSession((s) => (s ? { ...s, pending_schedule_setup: null } : s));
      }
      if (err instanceof ApiError && apiErrorCode(err) === "annual_to_monthly_pix_too_early") {
        setChangeError(t("integrated.annualToMonthlyPixTooEarly"));
        return;
      }
      if (err instanceof ApiError && apiErrorCode(err) === "premium_trial_running") {
        // PIT-535: Premium on credit until the trial's first charge; only "Voltar ao Básico" changes it.
        setChangeError(t("integrated.premiumTrial.changeBlocked"));
        return;
      }
      if (err instanceof ApiError && apiErrorCode(err) === "premium_trial_period_unpaid") {
        // After the trial was given up or stopped: annual waits for the date the trial set.
        const until = (err.body as { until?: unknown } | null)?.until;
        setChangeError(
          typeof until === "string"
            ? t("integrated.premiumTrial.annualAfterDate", { date: formatBillingDate(until, i18n.language) })
            : t("integrated.premiumTrial.annualAfterTrial"),
        );
        return;
      }
      if (err instanceof ApiError && apiErrorCode(err) === "invalid_cpf") {
        openCpfRetryDialog();
        return;
      }
      if (isPixUnavailableError(err)) {
        setChangeError(t("checkout.pixUnavailable"));
        setPixUnavailable(true);
        return;
      }
      if (err instanceof ApiError && apiErrorCode(err) === "stone_checkout_in_progress") {
        // A switch to Stone is still being charged: wait for it, never charge twice.
        setStoneCard(EMPTY_STONE_CARD);
        setStoneChangeNotice({ status: "pending" });
        return;
      }
      if (err instanceof ApiError && apiErrorCode(err) === "stone_change_not_available") {
        // The subscription no longer qualifies for the 12x: show what it can do now.
        setStoneCard(EMPTY_STONE_CARD);
        await reloadSession().catch(() => null);
        setChangeError(t("integrated.stoneChangeUnavailable"));
        return;
      }
      if (err instanceof ApiError && payWithStone) {
        const code = apiErrorCode(err);
        // A CPF just confirmed and then a card error: the message must not sit behind the dialog.
        setCpfDialogOpen(false);
        if (code === "card_declined") {
          // A declined card can be retried with the same or another card; the reason stays in the audit.
          setStoneCard((c) => ({ ...c, cvv: "" }));
          setChangeError(t("checkout.stone.errors.declined"));
          return;
        }
        if (code === "stone_checkout_failed") {
          setChangeError(t("checkout.stone.errors.failed"));
          return;
        }
        if (code === "too_many_attempts" || code === "rate_limit_exceeded") {
          setStoneCard(EMPTY_STONE_CARD);
          setChangeError(t("checkout.stone.errors.tooManyAttempts"));
          return;
        }
      }
      if (err instanceof ApiError && err.status === 409 && pendingScheduledChange) {
        setScheduledChangeSupportDialog({
          change: pendingScheduledChange,
          reason: "replace",
          requestedChangeDetails: describeFormRequestedChange(),
        });
      } else {
        setChangeError(err instanceof ApiError ? t("integrated.changeFailed") : t("common.error"));
      }
    } finally {
      setChangeSubmitting(false);
    }
  }

  async function applyChange() {
    if (selectionBlocksChange || changeSubmitting) return;
    if (annualToMonthlyPixBlocked) return;
    if (showActivePendingPix) {
      await openPendingPixOrder(activePendingPix.order_id, plan, billingInterval);
      return;
    }
    if (session?.customer.needs_phone && !isValidPhoneE164(fillPhone)) {
      alert(t("checkout.errors.phone"));
      return;
    }

    // The selection is the unfinished setup itself: finish it, as the phone's sticky does
    // (/change would answer "already scheduled" for it and the click would do nothing).
    if (
      pendingScheduleSetup &&
      findMatchingScheduledChange([pendingScheduleSetup], { plan, interval: billingInterval, paymentMethod })
    ) {
      await resumeScheduledProviderSetup(pendingScheduleSetup);
      return;
    }

    const matchingScheduled = findMatchingScheduledChange(session?.scheduled_changes ?? [], {
      plan,
      interval: billingInterval,
      paymentMethod,
    });
    if (matchingScheduled) {
      setAlreadyScheduledDialog(matchingScheduled);
      return;
    }

    if (pendingScheduledChange) {
      setScheduledChangeSupportDialog({
        change: pendingScheduledChange,
        reason: "replace",
        requestedChangeDetails: describeFormRequestedChange(),
      });
      return;
    }

    await executeChange();
  }

  async function confirmCpfDialog(cpfFormatted: string) {
    if (!isValidCPF(cpfFormatted)) {
      setCpfDialogError(t("checkout.errors.cpf"));
      return;
    }
    const cpfDigits = cpfFormatted.replace(/\D/g, "");
    setFillCpf(formatCPF(cpfDigits));
    // The dialog opened for "Testar 30 dias": the trial again, with the CPF.
    if (premiumTrialOfferRef.current) {
      await acceptPremiumTrial(cpfDigits);
      return;
    }
    await executeChange(cpfDigits);
  }

  if (loadErr?.kind === "offer_expired") {
    // The premium trial link after the offer's deadline (PIT-535): nothing is wrong with the account
    // and a new link from support would not bring the offer back.
    return (
      <CenteredCard>
        <div className="text-xl font-semibold mb-2">{t("integrated.loadErrorOfferExpiredTitle")}</div>
        <p className="text-sm text-ink-2 leading-relaxed">{t("integrated.loadErrorOfferExpiredBody")}</p>
      </CenteredCard>
    );
  }
  if (loadErr) {
    const linkIsBad = loadErr.kind === "link";
    return (
      <CenteredCard>
        <div className="text-xl font-semibold mb-2">
          {t(linkIsBad ? "integrated.loadErrorLinkTitle" : "integrated.loadErrorTemporaryTitle")}
        </div>
        <p className="text-sm text-ink-2 leading-relaxed">
          {t(linkIsBad ? "integrated.loadErrorLinkBody" : "integrated.loadErrorTemporaryBody")}
        </p>
        <div className="mt-5 flex flex-col gap-2">
          {!linkIsBad && (
            <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
              {t("integrated.loadErrorRetry")}
            </button>
          )}
          <a
            href={buildWhatsAppUrl(BILLING_SUPPORT_WHATSAPP_PHONE, t("integrated.loadErrorWhatsAppMessage"))}
            target="_blank"
            rel="noopener noreferrer"
            className={linkIsBad ? "btn-primary" : "btn-ghost"}
          >
            {t("integrated.loadErrorWhatsApp")}
          </a>
        </div>
      </CenteredCard>
    );
  }
  if (!session) {
    return (
      <main className="min-h-screen flex items-center justify-center p-4 surface-aurora">
        <BrandLoader message={t("common.loading")} />
      </main>
    );
  }

  if (inlinePix) {
    return (
      <main className="min-h-screen surface-aurora pb-28 lg:pb-12">
        <div className="mx-auto max-w-lg px-4 py-8 lg:py-12">
          <IntegratedPixPanel
            orderId={inlinePix.orderId}
            initialEmv={inlinePix.emv}
            plan={inlinePix.plan}
            interval={inlinePix.interval}
            {...(inlinePix.keep
              ? {
                  notice: t("integrated.keepPix.panelNotice"),
                  titleKey: "integrated.keepPix.panelTitle",
                  paidTitle: t("integrated.keepPix.keptTitle"),
                  paidMessage: t("integrated.keepPix.keptBody"),
                }
              : {})}
            {...(inlinePix.premiumTrial
              ? {
                  paidTitle: t("integrated.premiumTrial.pixApprovedTitle"),
                  paidMessage: t("integrated.premiumTrial.pixApprovedBody"),
                }
              : {})}
            authorizeOnly={inlinePix.authorizeOnly}
            onSwitchToCard={
              // Not for "Manter assinatura" (PIX only), nor for the premium trial (always on the
              // subscription's own method), nor for a switch from card to PIX on the same plan: by card
              // that is the subscription as it is, with nothing to confirm.
              inlinePix.keep ||
              inlinePix.premiumTrial ||
              (current &&
                !subscriptionExpired &&
                inlinePix.plan === current.plan &&
                inlinePix.interval === current.interval &&
                selectableMethod(current.paymentMethod) === "stripe")
                ? undefined
                : () => {
                    // Back to the picker on the same change, by card; no reloadSession, which would put
                    // the plan and method back to the subscription's. The customer confirms there.
                    const target = inlinePix;
                    setInlinePix(null);
                    setPlan(target.plan);
                    setBillingInterval(target.interval);
                    setPaymentMethod("stripe");
                    setChangeError(null);
                    setPixUnavailable(false);
                  }
            }
            onBack={() => {
              setInlinePix(null);
              syncSessionAfterPixFlow();
            }}
            onPaid={(order) => {
              notifyParentBillingUpdated({
                subscription_plan: (order.plan as Plan | undefined) ?? inlinePix.plan,
                subscription_period: (order.interval as BillingInterval | undefined) ?? inlinePix.interval,
                payment_method: "openpix",
              });
              window.setTimeout(() => {
                setInlinePix(null);
                syncSessionAfterPixFlow();
              }, 1200);
            }}
          />
        </div>
      </main>
    );
  }

  if (cardUpdateOpen && session.card_update) {
    return (
      <main className="min-h-screen surface-aurora pb-28 lg:pb-12">
        <div className="mx-auto max-w-lg px-4 py-8 lg:py-12">
          <CardUpdatePanel
            token={token}
            pastDue={session.card_update.past_due}
            onBack={() => {
              setCardUpdateOpen(false);
              void reloadSession();
            }}
            onDone={() => {
              postBillingUpdatedToParent();
            }}
          />
        </div>
      </main>
    );
  }

  if (inlineStripe) {
    return (
      <main className="min-h-screen surface-aurora pb-28 lg:pb-12">
        <div className="mx-auto max-w-lg px-4 py-8 lg:py-12">
          <IntegratedStripePanel
            orderId={inlineStripe.orderId}
            plan={inlineStripe.plan}
            interval={inlineStripe.interval}
            initialClientSecret={inlineStripe.clientSecret}
            initialPublishableKey={inlineStripe.publishableKey}
            initialFinalCents={inlineStripe.finalCents}
            onBack={() => {
              setInlineStripe(null);
              void reloadSession();
            }}
            onPaid={(order) => {
              notifyParentBillingUpdated({
                subscription_plan: (order.plan as Plan | undefined) ?? inlineStripe.plan,
                subscription_period: (order.interval as BillingInterval | undefined) ?? inlineStripe.interval,
                payment_method: "stripe",
              });
              window.setTimeout(() => {
                setInlineStripe(null);
                void reloadSession();
              }, 1200);
            }}
          />
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen surface-aurora pb-28 lg:pb-12">
      <div className="mx-auto max-w-5xl px-4 py-8 lg:py-12">
        {supportBooking && (
          <div className="mb-6 space-y-2">
            <SupportBookingCard
              booking={supportBooking}
              locale={i18n.language || "pt-BR"}
              title={t("integrated.cancelBookingCardTitle")}
              body={t("integrated.cancelBookingCardBody")}
              rescheduleLabel={t("integrated.cancelBookingCardReschedule")}
              cancelLabel={t("integrated.cancelBookingCardCancel")}
              busy={supportBookingBusy}
              onReschedule={() => {
                setCancelDialogIntent("reschedule");
                setConfirmCancelOpen(true);
              }}
              onCancel={() => {
                setSupportBookingError(null);
                setConfirmCancelBookingOpen(true);
              }}
            />
            {supportBookingError && <p className="text-sm text-danger">{supportBookingError}</p>}
          </div>
        )}
        {confirmCancelBookingOpen && supportBooking && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="card-modal max-w-sm w-full p-6 space-y-4 text-[color:var(--text-primary)]">
              <p className="text-base font-medium">{t("integrated.cancelManageCancelBookingConfirm")}</p>
              <div className="flex gap-2 justify-end flex-wrap">
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={supportBookingBusy}
                  onClick={() => setConfirmCancelBookingOpen(false)}
                >
                  {t("integrated.cancelManageKeep")}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={supportBookingBusy}
                  onClick={() => {
                    void (async () => {
                      setSupportBookingBusy(true);
                      setSupportBookingError(null);
                      try {
                        await api(`/api/integrated/cancel-rescue/bookings/${supportBooking.id}/cancel`, {
                          method: "POST",
                          body: JSON.stringify({ token }),
                        });
                        setSupportBooking(null);
                        setConfirmCancelBookingOpen(false);
                      } catch {
                        setSupportBookingError(t("integrated.cancelErrorGeneric"));
                        setConfirmCancelBookingOpen(false);
                      } finally {
                        setSupportBookingBusy(false);
                      }
                    })();
                  }}
                >
                  {supportBookingBusy
                    ? t("integrated.cancelBusy")
                    : t("integrated.cancelManageCancelBookingYes")}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="card-glass p-6 lg:p-8 text-[color:var(--text-primary)] mb-6">
          <header>
            {/* A trial-only session's email is masked: never shown (PIT-535). */}
            {!trialOnly && (
              <div className="text-xs uppercase tracking-widest text-ink-2">{session.customer.email}</div>
            )}
            <h1 className="text-2xl lg:text-3xl font-semibold mt-2" style={{ letterSpacing: "-0.02em" }}>
              {t("integrated.manageTitle")}
            </h1>
            {current && (
              <p className="text-sm mt-3 rounded-lg bg-black/[0.04] px-3 py-2.5">
                <span className="text-ink-2">
                  {subscriptionExpired ? t("integrated.lastPlan") : t("integrated.currentPlan")}:{" "}
                </span>
                <span className="font-medium">
                  {t(`plans.${current.plan}`)} · {t(`plans.${current.interval}`)} ·{" "}
                  {money(current.periodAmountCents)}
                </span>
              </p>
            )}
          </header>

          {!trialOnly &&
            session.card_update &&
            (session.card_update.past_due ? (
              <div className="mt-5 rounded-lg border border-danger/35 bg-danger/[0.08] p-4" role="status">
                <div className="font-semibold text-sm text-danger">{t("integrated.cardUpdate.pastDueTitle")}</div>
                <p className="text-sm text-ink-2 mt-2 leading-relaxed">{t("integrated.cardUpdate.pastDueBody")}</p>
                <button type="button" className="btn-primary text-sm mt-3" onClick={() => setCardUpdateOpen(true)}>
                  {t("integrated.cardUpdate.button")}
                </button>
              </div>
            ) : (
              <div className="mt-4">
                <button type="button" className="btn-ghost text-xs px-3 py-1.5" onClick={() => setCardUpdateOpen(true)}>
                  {t("integrated.cardUpdate.button")}
                </button>
              </div>
            ))}

          {!trialOnly && stoneNotice && (
            <div
              className={`mt-5 rounded-lg border p-4 ${
                stoneNotice.status === "paid"
                  ? "border-emerald-600/35 bg-emerald-600/[0.08]"
                  : "border-warning/40 bg-warning/[0.06]"
              }`}
              role="status"
            >
              {stoneNotice.status === "paid" ? (
                <>
                  <div className="font-semibold text-sm text-emerald-900 dark:text-emerald-100">
                    {t("integrated.stoneChangePaidTitle", { plan: t(`plans.${stoneNotice.plan}`) })}
                  </div>
                  {stoneNotice.renewsAt && (
                    <p className="text-sm text-ink-2 mt-2 leading-relaxed">
                      {t("integrated.stoneChangePaidBody", {
                        date: formatBillingDate(stoneNotice.renewsAt, i18n.language),
                      })}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-ink-2 leading-relaxed">{t("integrated.stoneChangePending")}</p>
              )}
            </div>
          )}

          {session.premium_trial && current ? (
            <PremiumTrialPanel
              state={session.premium_trial}
              paymentMethod={current.paymentMethod}
              money={trialMoney}
              beforeAccept={
                // The full page asks for the phone in its own block below; the trial-only page has none.
                trialOnly && premiumTrialNeedsPhone ? (
                  <label className="block mt-3">
                    <span className="field-label">{t("checkout.phone")}</span>
                    <PhoneInput value={fillPhone} onChange={setFillPhone} required />
                    <span className="block text-xs text-ink-2 mt-1">{t("integrated.premiumTrial.phoneHint")}</span>
                  </label>
                ) : null
              }
              formatDate={(iso) => formatBillingDate(iso, i18n.language)}
              busy={premiumTrialBusy || changeSubmitting || openingPayment}
              error={premiumTrialError}
              onAccept={startPremiumTrial}
              onResumePix={resumePremiumTrialPix}
              onGiveUp={() => void giveUpPremiumTrial()}
            />
          ) : premiumTrialError ? (
            // The refusal took the trial off the page (expired, used, no longer eligible): say why.
            <div className="mt-5 rounded-lg border border-warning/40 bg-warning/[0.06] p-4" role="alert">
              <p className="text-sm leading-relaxed">{premiumTrialError}</p>
              {trialOnly && (
                <p className="text-xs text-ink-2 mt-2 leading-relaxed">{t("integrated.premiumTrial.trialOnlyManageHint")}</p>
              )}
            </div>
          ) : (
            trialOnly && (
              <div className="mt-5 rounded-lg border border-black/[0.08] bg-black/[0.02] p-4" role="status">
                <p className="text-sm leading-relaxed">{t("integrated.premiumTrial.trialOnlyUnavailable")}</p>
              </div>
            )
          )}

          {inPlaceUpgradeNotice && (
            <div
              ref={inPlaceUpgradeNoticeRef}
              className="mt-5 rounded-lg border border-emerald-600/35 bg-emerald-600/[0.08] p-4 scroll-mt-24"
              role="status"
            >
              <div className="font-semibold text-sm text-emerald-900 dark:text-emerald-100">
                {t("integrated.inPlaceUpgradeTitle")}
              </div>
              <p className="text-sm text-ink-2 mt-2 leading-relaxed">
                {t("integrated.inPlaceUpgradeBody", {
                  plan: t(`plans.${inPlaceUpgradeNotice.plan}`),
                  interval: t(`plans.${inPlaceUpgradeNotice.interval}`),
                  date: formatBillingDate(inPlaceUpgradeNotice.firstChargeAt, i18n.language),
                  amount: money(inPlaceUpgradeNotice.recurringCents),
                })}
              </p>
              <p className="text-xs text-ink-2 mt-2 leading-relaxed">
                {t("integrated.inPlaceUpgradeHint")}
              </p>
              <button
                type="button"
                className="btn-ghost text-xs px-3 py-1.5 mt-3"
                onClick={() => setInPlaceUpgradeNotice(null)}
              >
                {t("integrated.inPlaceUpgradeDismiss")}
              </button>
            </div>
          )}

          {!trialOnly && showSubscriptionExpiredBanner && (
            <div className="mt-5 rounded-lg border border-danger/35 bg-danger/[0.08] p-4">
              <div className="font-semibold text-sm text-danger">{t("integrated.subscriptionExpiredTitle")}</div>
              <p className="text-sm text-ink-2 mt-2 leading-relaxed">
                {t("integrated.subscriptionExpiredBody", {
                  date: session.zenpost_state.date_payments_ended
                    ? formatBillingDate(session.zenpost_state.date_payments_ended, i18n.language)
                    : "—",
                })}
              </p>
              {expiredNothingToCancel && (
                <p className="text-sm font-medium mt-2 leading-relaxed">{t("integrated.subscriptionExpiredNoCharge")}</p>
              )}
              {!expiredNothingToCancel && expiredCancelRecorded && (
                <p className="text-sm font-medium mt-2 leading-relaxed">{t("integrated.subscriptionExpiredCancelRecorded")}</p>
              )}
              <p className="text-xs text-ink-2 mt-2 leading-relaxed">{t("integrated.subscriptionExpiredHint")}</p>
            </div>
          )}

          {!trialOnly && appliedScheduledChangeNotice.length > 0 && (
            <div className="mt-5 rounded-lg border border-warning/40 bg-warning/[0.08] p-4">
              <div className="font-semibold text-sm">
                {justAppliedCancellation
                  ? t("integrated.scheduledCancellationAppliedTitle")
                  : t("integrated.scheduledChangeAppliedTitle")}
              </div>
              <ul className="mt-2 space-y-2 text-sm text-ink-2 leading-relaxed">
                {appliedScheduledChangeNotice.map((change) => (
                  <li key={change.id}>
                    {describeAppliedScheduledChange(change, t, i18n.language)}
                  </li>
                ))}
              </ul>
              {!justAppliedCancellation && (
                <p className="text-xs text-ink-2 mt-2 leading-relaxed">
                  {t("integrated.scheduledChangeAppliedHint")}
                </p>
              )}
            </div>
          )}

          {!trialOnly && pendingScheduleSetup && (
            <div className="mt-5 pt-5 border-t border-black/10 scroll-mt-24">
              <div className="rounded-lg border border-purple-deep/25 bg-purple-deep/5 p-4">
                <div className="font-semibold text-sm">{t("integrated.pendingProviderSetupTitle")}</div>
                <p className="text-sm text-ink-2 mt-2">
                  {describeScheduledChange(pendingScheduleSetup, t, i18n.language)}
                </p>
                <p className="text-xs text-warning mt-1.5 leading-relaxed">
                  {openingPayment ? t("integrated.openingPaymentHint") : t("integrated.scheduledChangePendingApproval")}
                </p>
                <button
                  type="button"
                  className="btn-primary text-xs px-3 py-1.5 mt-3"
                  disabled={openingPayment}
                  aria-busy={openingPayment}
                  onClick={() => void resumeScheduledProviderSetup(pendingScheduleSetup)}
                >
                  {openingPayment
                    ? t("integrated.processing")
                    : pendingScheduleSetup.payload?.target_payment_method === "openpix"
                      ? t("integrated.approvePixNow")
                      : t("integrated.completeCardSetupNow")}
                </button>
                {changeError && <p className="text-xs text-danger mt-2">{changeError}</p>}
              </div>
            </div>
          )}

          {!trialOnly && session.scheduled_changes.length > 0 && (
            <div ref={scheduledChangesRef} className="mt-5 pt-5 border-t border-black/10 scroll-mt-24">
              <div
                className={`rounded-lg border border-warning/40 bg-warning/[0.08] p-4 transition-colors ${
                  scheduledChangesHighlight === "added" ? "integrated-scheduled-changes--highlight-add" : ""
                }`}
              >
                <div className="font-semibold text-sm">{t("integrated.scheduledChanges")}</div>
                <ul className="mt-2 space-y-2">
                  {session.scheduled_changes.map((s) => (
                    <li key={s.id} className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <span className="text-ink-2">{describeScheduledChange(s, t, i18n.language)}</span>
                        {s.kind === "cancellation" &&
                          session.pix_keep?.enabled &&
                          session.pix_keep.pending_order_id && (
                            <p className="text-ink-2 mt-1">{t("integrated.keepPix.pendingNote")}</p>
                          )}
                        {s.kind === "cancellation" && session.cancellation_refund && (
                          <p className="text-ink-2 mt-1">
                            {t("integrated.cancelRefundStillAvailable", {
                              amount: formatRefundMoney(session.cancellation_refund.amount_cents, session.cancellation_refund, i18n.language),
                              deadline: formatRefundDeadline(session.cancellation_refund.refundable_until, t, i18n.language),
                            })}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0 self-start">
                      {s.kind === "cancellation" && session.cancellation_refund && (
                        <button
                          type="button"
                          className="btn-ghost text-xs px-3 py-1"
                          onClick={() => setLateRefundOffer(session.cancellation_refund ?? null)}
                        >
                          {t("integrated.lateRefundButton")}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-ghost text-xs px-3 py-1"
                        onClick={() => {
                          if (
                            canSelfServiceReverseScheduledCancellation(s, new Date(), {
                              paymentMethod: current?.paymentMethod,
                            })
                          ) {
                            setReverseCancellationDialog(s);
                            return;
                          }
                          if (
                            s.kind === "cancellation" &&
                            current?.paymentMethod === "openpix" &&
                            session.pix_keep?.enabled
                          ) {
                            setKeepPixDialog(s);
                            return;
                          }
                          if (session.can_raise_in_place && s.kind === "downgrade_plan") {
                            setKeepPremiumDialog(s);
                            return;
                          }
                          setScheduledChangeSupportDialog({
                            change: s,
                            reason:
                              s.kind === "cancellation" && current?.paymentMethod === "openpix"
                                ? "reverse_openpix_cancellation"
                                : "cancel",
                          });
                        }}
                      >
                        {s.kind === "cancellation"
                          ? t("integrated.reverseCancellationConfirm")
                          : getCancelScheduledChangeActionLabel(s, current, t)}
                      </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <PendingScheduleBlocksChangeMessage
                  className="mt-3 text-xs text-ink-2 leading-relaxed border-t border-warning/25 pt-3"
                  whatsappUrl={scheduledChangeHelpWhatsAppUrl}
                />
              </div>
            </div>
          )}
          {trialOnly && session.premium_trial && current && (
            <p className="text-xs text-ink-2 mt-5 leading-relaxed">{t("integrated.premiumTrial.trialOnlyManageHint")}</p>
          )}
        </div>

        {/* The trial link's page (PIT-535) is the plan and the trial only: its session cannot change
            plans, pay, cancel or see payments, so none of that is offered. */}
        {!trialOnly && (
          <>
            <div className="grid lg:grid-cols-[1fr_380px] gap-6 lg:gap-8 items-start">
              <div className={`space-y-6 ${changesLocked ? "integrated-changes-locked" : ""}`}>
                <section
                  ref={planPickerRef}
                  className="card-glass p-6 lg:p-8 text-[color:var(--text-primary)] scroll-mt-6"
                  aria-disabled={changesLocked}
                >
                  <div className="integrated-step-label">{t("integrated.stepChoosePlan")}</div>
                  <div className="flex justify-center mb-5">
                    <PlanIntervalToggle
                      value={billingInterval}
                      onChange={setBillingInterval}
                      disabled={changesLocked}
                    />
                  </div>
                  <PlanComparisonTable
                    mode="integrated"
                    interval={billingInterval}
                    selectedPlan={plan}
                    onSelectPlan={setPlan}
                    currentPlan={current?.plan}
                    currentInterval={current?.interval}
                    priceDeltaByPlan={planPriceDeltas}
                    disabled={changesLocked}
                  />
                  {billingInterval === "monthly" && getAnnualSavings(plan, currency).savingsCents > 0 && !changesLocked && (
                    <div className="mt-5 pt-4 border-t border-black/10">
                      <button
                        type="button"
                        onClick={() => setBillingInterval("annual")}
                        className="text-sm w-full text-left rounded-lg border border-black/10 px-3 py-2.5 hover:bg-black/[0.03] transition-colors"
                      >
                        <span className="font-medium">{t("checkout.upsellTitleShort")}</span>
                        <span className="text-ink-2 text-xs block mt-0.5">
                          {t("checkout.upsellBody", {
                            amount: money(getPlanPricing(plan, "annual", currency).totalCents),
                            monthly: money(getPlanPricing(plan, "annual", currency).monthlyEquivalentCents),
                          })}
                        </span>
                      </button>
                    </div>
                  )}
                </section>

                <div className="lg:hidden">
                  <ChangePreviewPanel
                    preview={changePreview}
                    breakdown={pricing}
                    prorationCents={prorationCents}
                    selectionBlocksChange={selectionBlocksChange}
                    previewMatches={previewMatches}
                    isScheduledChange={showsScheduledPricing}
                    dueTodayLabel={dueTodayLabel}
                    pricingLoading={pricingLoading}
                    pricingError={pricingError}
                    confirmLabel={confirmLabel}
                    raiseInPlaceHint={raiseInPlaceHint}
                    submitting={changeSubmitting}
                    changeError={changeError}
                    annualToMonthlyPixBlocked={annualToMonthlyPixBlocked}
                    changesLocked={changesLocked}
                    premiumTrialLocked={premiumTrialLocksChanges && !approvedScheduledChange}
                    supportWhatsAppUrl={scheduledChangeHelpWhatsAppUrl}
                    onConfirm={applyChange}
                    showConfirm={false}
                    stone={stonePanel}
                    onPayByCard={payByCard}
                  />
                </div>

                {(pixLocaleAvailable || showActivePendingPix) && (
                  <section className="card-glass p-6 lg:p-8 text-[color:var(--text-primary)] space-y-4">
                    {pixLocaleAvailable && (
                      <>
                        <div className="integrated-step-label">{t("integrated.stepPaymentMethod")}</div>
                        {current && paymentMethod === current.paymentMethod && (
                          <p className="text-xs text-ink-2">
                            {t("integrated.paymentMethodCurrent", {
                              method: paymentMethod === "openpix" ? t("checkout.pix") : t("checkout.card"),
                            })}
                          </p>
                        )}
                        <PaymentMethodTabs
                          value={paymentMethod}
                          onChange={setPaymentMethod}
                          pixDisabled={!pixAvailable}
                          disabled={changesLocked}
                        />
                        {!pixAvailable && (
                          <p className="text-xs text-ink-2">{t("checkout.pixBrazilOnly")}</p>
                        )}
                      </>
                    )}
                    {showActivePendingPix && (
                      <ResumePixBanner
                        order={{
                          pix_expires_at: activePendingPix.pix_expires_at,
                        }}
                        titleKey={
                          activePendingPix.final_cents === 0
                            ? "integrated.resumePixAuthorizeTitle"
                            : "checkout.resumePixTitle"
                        }
                        hintKey={
                          activePendingPix.final_cents === 0
                            ? "integrated.resumePixAuthorizeHint"
                            : "checkout.resumePixHint"
                        }
                        hintNoTimerKey={
                          activePendingPix.final_cents === 0
                            ? "integrated.resumePixAuthorizeHintNoTimer"
                            : "checkout.resumePixHintNoTimer"
                        }
                        onExpired={() => setActivePixBannerDismissed(true)}
                        loading={openingPayment}
                        onResume={() => void openPendingPixOrder(activePendingPix.order_id, plan, billingInterval)}
                      />
                    )}
                  </section>
                )}

                <section className="card-glass p-6 lg:p-8 text-[color:var(--text-primary)] space-y-4">
                  {pricingError && (
                    <p className="text-sm text-danger">
                      {pricingError === "contact_support" ? t("integrated.changeContactSupport") : t("common.error")}
                    </p>
                  )}

                  {!changesLocked && (
                    <CouponSection
                      open={couponOpen}
                      draft={couponDraft}
                      applied={couponApplied}
                      discountCents={pricing?.discountCents ?? 0}
                      error={couponError}
                      onToggle={() => setCouponOpen((v) => !v)}
                      onChangeDraft={setCouponDraft}
                      onApply={applyCoupon}
                      onRemove={removeCoupon}
                    />
                  )}

                  {!changesLocked && showCompleteData && (
                    <div className="rounded-xl border border-warning/40 bg-warning/[0.06] p-4 space-y-3">
                      <div className="text-sm font-semibold">{t("integrated.completeYourData")}</div>
                      <p className="text-xs text-ink-2">{t("integrated.completeYourDataHelp")}</p>
                      <label className="block">
                        <span className="field-label">{t("checkout.phone")}</span>
                        <PhoneInput value={fillPhone} onChange={setFillPhone} required />
                      </label>
                    </div>
                  )}

                </section>
              </div>

              <aside className="hidden lg:block lg:sticky lg:top-8">
                <ChangePreviewPanel
                  preview={changePreview}
                  breakdown={pricing}
                  prorationCents={prorationCents}
                  selectionBlocksChange={selectionBlocksChange}
                  previewMatches={previewMatches}
                  isScheduledChange={showsScheduledPricing}
                  dueTodayLabel={dueTodayLabel}
                  pricingLoading={pricingLoading}
                  pricingError={pricingError}
                  confirmLabel={confirmLabel}
                  raiseInPlaceHint={raiseInPlaceHint}
                  submitting={changeSubmitting}
                  changeError={changeError}
                  annualToMonthlyPixBlocked={annualToMonthlyPixBlocked}
                  changesLocked={changesLocked}
                  premiumTrialLocked={premiumTrialLocksChanges && !approvedScheduledChange}
                  hasActivePendingPix={!!showActivePendingPix}
                  supportWhatsAppUrl={scheduledChangeHelpWhatsAppUrl}
                  onConfirm={applyChange}
                  showConfirm
                  stone={stonePanel}
                  onPayByCard={payByCard}
                />
              </aside>
            </div>

            <section className="mt-10">
              <h2 className="text-lg font-semibold mb-3 text-white">{t("integrated.paymentHistory")}</h2>
              <div className="card-glass overflow-hidden">
                <ul className="divide-y divide-black/5">
                  {session.payments.length === 0 ? (
                    <li className="px-4 py-3 text-sm text-ink-2">—</li>
                  ) : (
                    session.payments.map((p) => (
                      <li key={p.id} className="px-4 py-3 flex justify-between gap-4 text-sm">
                        <span className="text-ink-2">
                          {formatBillingDate(p.paidAt, i18n.language)} · {formatPaymentDescription(p.description, t)}
                          {p.status === "refunded" && (
                            <span className="text-success"> ({t("integrated.paymentHistoryRefunded")})</span>
                          )}
                        </span>
                        <span className={`font-mono font-medium shrink-0 ${p.status === "refunded" ? "text-success" : ""}`}>
                          {p.status === "refunded" ? "− " : ""}
                          {money(p.amountCents)}
                        </span>
                      </li>
                    ))
                  )}
                  {showCancelPlan && (
                  <li className="px-4 py-3">
                    <button
                      type="button"
                      className="text-sm text-ink-2 underline text-left hover:text-[color:var(--text-primary)] transition-colors disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                      disabled={!!approvedScheduledChange}
                      title={approvedScheduledChange ? t("integrated.pendingScheduleBlocksChange") : undefined}
                      onClick={() => {
                        if (approvedScheduledChange) {
                          setScheduledChangeSupportDialog({
                            change: approvedScheduledChange,
                            reason: "replace",
                            requestedChangeDetails: t("integrated.cancelPlan"),
                          });
                          return;
                        }
                        setCancelDialogIntent("flow");
                        setConfirmCancelOpen(true);
                      }}
                    >
                      {t("integrated.cancelPlan")}
                    </button>
                  </li>
                  )}
                </ul>
              </div>
            </section>
          </>
        )}

        {confirmCancelOpen && (
          <CancelDialog
            token={token}
            email={session?.customer.email ?? ""}
            intent={cancelDialogIntent}
            refundOffer={session?.cancellation_refund ?? null}
            // Over: no "active until" date to promise, and no plan to price a change against.
            periodEnd={subscriptionExpired ? null : (current?.currentPeriodEnd ?? null)}
            subscriptionOver={subscriptionExpired}
            cheaperPlanCurrent={subscriptionExpired ? null : cancelDialogCurrent}
            planChange={cancelPlanChange}
            appLanguage={appLanguage}
            money={money}
            stoneChange={Boolean(session?.stone_change)}
            onPickCheaperPlan={(target, withGift) => {
              setConfirmCancelOpen(false);
              setGiftWithChange(withGift ? { plan: target.plan, interval: target.interval } : null);
              setPlan(target.plan);
              setBillingInterval(target.interval);
              // The 12x is the card on annual: the picker opens on it with the card form.
              if (target.paymentMethod === "stone") setPaymentMethod("stripe");
              window.setTimeout(() => {
                planPickerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 50);
            }}
            onClose={() => setConfirmCancelOpen(false)}
            onBookingChange={(next) => setSupportBooking(next)}
            onRefundWindowClosed={async () => {
              await reloadSession().catch(() => null);
            }}
            onDone={async () => {
              postBillingUpdatedToParent("cancellation");
              const r = await reloadSession();
              if (r?.scheduled_changes.length) {
                setScheduledChangesHighlight("added");
                window.setTimeout(() => {
                  scheduledChangesRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 100);
              }
            }}
          />
        )}

        {scheduledChangeSupportDialog && (
          <ScheduledChangeSupportDialog
            change={scheduledChangeSupportDialog.change}
            current={current}
            reason={scheduledChangeSupportDialog.reason}
            email={session.customer.email}
            requestedChangeDetails={scheduledChangeSupportDialog.requestedChangeDetails}
            onClose={() => setScheduledChangeSupportDialog(null)}
          />
        )}

        {lateRefundOffer && (
          <LateRefundDialog
            token={token}
            offer={lateRefundOffer}
            periodEnd={current?.currentPeriodEnd ?? null}
            onClose={() => setLateRefundOffer(null)}
            onDone={async () => {
              postBillingUpdatedToParent("cancellation");
              await reloadSession().catch(() => null);
            }}
            onWindowClosed={async () => {
              await reloadSession().catch(() => null);
            }}
          />
        )}

        {reverseCancellationDialog && (
          <ReverseScheduledCancellationDialog
            token={token}
            change={reverseCancellationDialog}
            current={current}
            onClose={() => setReverseCancellationDialog(null)}
            onDone={async () => {
              postBillingUpdatedToParent();
              await reloadSession();
            }}
          />
        )}

        {keepPixDialog && session.pix_keep?.enabled && (
          <KeepPixSubscriptionDialog
            token={token}
            changeId={keepPixDialog.id}
            effectiveAt={keepPixDialog.effectiveAt}
            keep={session.pix_keep}
            onAuthorization={({ orderId, emv }) => {
              setKeepPixDialog(null);
              if (!current) return;
              setInlinePix({ orderId, emv, plan: current.plan, interval: current.interval, keep: true });
            }}
            onSupport={() => {
              const change = keepPixDialog;
              setKeepPixDialog(null);
              setScheduledChangeSupportDialog({ change, reason: "reverse_openpix_cancellation" });
            }}
            onClose={() => setKeepPixDialog(null)}
          />
        )}

        {keepPremiumDialog && (
          <KeepPremiumRaiseDialog
            change={keepPremiumDialog}
            amountLabel={money(
              session?.raise_in_place_cents ?? getPlanPricing("premium", "monthly", currency).totalCents,
            )}
            submitting={changeSubmitting}
            error={changeError}
            onClose={() => {
              if (!changeSubmitting) setKeepPremiumDialog(null);
            }}
            onConfirm={async () => {
              await keepPremiumInPlace(keepPremiumDialog.id);
            }}
          />
        )}

        {alreadyScheduledDialog && (
          <AlreadyScheduledChangeDialog
            change={alreadyScheduledDialog}
            onClose={closeAlreadyScheduledDialog}
          />
        )}

        {cpfDialogOpen && (
          <CpfCheckoutDialog
            initialCpf={fillCpf || (session.customer.cpf && !trialOnly ? formatCPF(session.customer.cpf) : "")}
            submitting={changeSubmitting || premiumTrialBusy}
            error={cpfDialogError}
            forCard={payWithStone && !premiumTrialOfferRef.current}
            onClose={() => {
              if (changeSubmitting || premiumTrialBusy) return;
              // Closing it drops the trial tap: a later plan change's CPF retry must not take the trial.
              premiumTrialOfferRef.current = null;
              setCpfDialogOpen(false);
            }}
            onConfirm={confirmCpfDialog}
          />
        )}
      </div>

      {!trialOnly && (
        <PaymentStickyBar
          amountLabel={payWithStone ? t("integrated.stoneChangeStickyLabel") : t("integrated.dueToday")}
          amount={payWithStone ? money(stoneInstallments!.first_year_installment_cents) : dueTodayLabel}
          buttonLabel={
            changeSubmitting || openingPayment
              ? t("integrated.processing")
              : approvedScheduledChange
                ? t("integrated.contactSupportWhatsApp")
                : resumesPendingSetup && pendingScheduleSetup
                  ? pendingScheduleSetup.payload?.target_payment_method === "openpix"
                    ? t("integrated.approvePixNow")
                    : t("integrated.completeCardSetupNow")
              : annualToMonthlyPixBlocked
                ? t("integrated.annualToMonthlyPixBlockedCta")
              : showActivePendingPix
                ? t("checkout.resumePixCta")
                : subscriptionExpired
                  ? t("integrated.confirmReactivation")
                : selectionUnchanged
                  ? t("integrated.noChanges")
                  : confirmLabel
          }
          disabled={
            openingPayment ||
            premiumTrialLocksChanges ||
            (selectionBlocksChange && !resumesPendingSetup && !showActivePendingPix) ||
            changeSubmitting ||
            (dueTodayCents == null && !showActivePendingPix && !(resumesPendingSetup && pendingScheduleSetup?.pending_order_id)) ||
            annualToMonthlyPixBlocked
          }
          onClick={() => {
            if (approvedScheduledChange) {
              setScheduledChangeSupportDialog({
                change: approvedScheduledChange,
                reason: "replace",
                requestedChangeDetails: selectionUnchanged
                  ? undefined
                  : describeFormRequestedChange(),
              });
              return;
            }
            if (resumesPendingSetup && pendingScheduleSetup) {
              void resumeScheduledProviderSetup(pendingScheduleSetup);
              return;
            }
            if (showActivePendingPix) {
              void openPendingPixOrder(activePendingPix!.order_id, plan, billingInterval);
              return;
            }
            void applyChange();
          }}
        />
      )}
    </main>
  );
}

function PaymentMethodTabs({
  value,
  onChange,
  pixDisabled,
  disabled,
}: {
  value: PaymentMethod;
  onChange: (v: PaymentMethod) => void;
  pixDisabled?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={`method-tabs ${disabled ? "opacity-60 pointer-events-none" : ""}`} role="tablist" aria-label="payment method">
      <button
        type="button"
        role="tab"
        aria-selected={value === "openpix"}
        data-active={value === "openpix"}
        className="method-tab"
        disabled={pixDisabled || disabled}
        title={pixDisabled ? t("checkout.pixBrazilOnly") : undefined}
        onClick={() => {
          if (!pixDisabled) onChange("openpix");
        }}
      >
        <Zap className="w-4 h-4" />
        <span>{t("checkout.pixLong")}</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === "stripe"}
        data-active={value === "stripe"}
        className="method-tab"
        disabled={disabled}
        onClick={() => onChange("stripe")}
      >
        <CreditCard className="w-4 h-4" />
        <span>{t("checkout.cardLong")}</span>
      </button>
    </div>
  );
}

function formatPeriodPrice(
  cents: number,
  interval: BillingInterval,
  t: (key: string, opts?: Record<string, string>) => string,
  locale: string,
) {
  return interval === "monthly"
    ? t("integrated.perPeriodMonthly", { value: formatMoney(cents, locale) })
    : t("integrated.perPeriodAnnual", { value: formatMoney(cents, locale) });
}

function ChangePreviewPanel({
  preview,
  breakdown,
  prorationCents,
  selectionBlocksChange,
  previewMatches,
  isScheduledChange,
  dueTodayLabel,
  pricingLoading,
  pricingError,
  confirmLabel,
  raiseInPlaceHint = null,
  submitting,
  changeError,
  annualToMonthlyPixBlocked = false,
  changesLocked = false,
  premiumTrialLocked = false,
  hasActivePendingPix = false,
  supportWhatsAppUrl,
  onConfirm,
  showConfirm,
  stone = null,
  onPayByCard,
}: {
  preview: ChangePreview | null;
  breakdown: PricingBreakdown | null;
  prorationCents: number;
  selectionBlocksChange: boolean;
  previewMatches: boolean;
  isScheduledChange: boolean;
  dueTodayLabel: string;
  pricingLoading?: boolean;
  pricingError?: string | null;
  confirmLabel: string;
  raiseInPlaceHint?: string | null;
  submitting?: boolean;
  changeError?: string | null;
  annualToMonthlyPixBlocked?: boolean;
  changesLocked?: boolean;
  /** The lock is the premium trial's (PIT-535), not a scheduled change: says so instead. */
  premiumTrialLocked?: boolean;
  hasActivePendingPix?: boolean;
  supportWhatsAppUrl?: string;
  onConfirm: () => void;
  showConfirm: boolean;
  /** Monthly to annual 12x on Stone: what the card is charged and the card fields. */
  stone?: {
    installments: StoneInstallments;
    card: StoneCardFields;
    onCardChange: (next: StoneCardFields) => void;
    /** Inside the 7-day window: the first month's refund is given up, as on any change to annual. */
    inRefundWindow: boolean;
  } | null;
  /** Woovi failed to create the PIX: a button under the error switches the change to card. */
  onPayByCard?: () => void;
}) {
  const { t, i18n } = useTranslation();

  if (changesLocked && premiumTrialLocked) {
    return (
      <div className="card-glass p-6 lg:p-8 text-[color:var(--text-primary)] space-y-4">
        <div className="text-sm font-semibold">{t("integrated.changePreviewTitle")}</div>
        <p className="text-sm text-ink-2 leading-relaxed">{t("integrated.premiumTrial.changeBlocked")}</p>
      </div>
    );
  }

  if (changesLocked) {
    return (
      <div className="card-glass p-6 lg:p-8 text-[color:var(--text-primary)] space-y-4">
        <div className="text-sm font-semibold">{t("integrated.scheduledChanges")}</div>
        <PendingScheduleBlocksChangeMessage
          className="text-sm text-ink-2 leading-relaxed"
          whatsappUrl={supportWhatsAppUrl}
        />
      </div>
    );
  }

  const showUpgradeBreakdown =
    previewMatches && !!preview && preview.decision === "immediate_upgrade" && !selectionBlocksChange;
  const isImmediateUpgrade =
    previewMatches && !!preview && preview.decision === "immediate_upgrade" && !selectionBlocksChange;
  const crossInterval =
    !!preview && preview.currentInterval !== preview.targetInterval;

  return (
    <div className="card-glass p-6 lg:p-8 text-[color:var(--text-primary)] space-y-4">
      <div className="text-sm font-semibold">{t("integrated.changePreviewTitle")}</div>

      {selectionBlocksChange || !preview ? (
        <p className="text-sm text-ink-2">{t("integrated.noChanges")}</p>
      ) : !previewMatches ? (
        <p className="text-sm text-ink-2">{pricingLoading ? t("common.loading") : t("integrated.calculatingPrice")}</p>
      ) : (
        <div className="space-y-2.5">
          <div className="integrated-preview-row">
            <span className="text-ink-2">{t("integrated.currentPlanLabel")}</span>
            <span className="text-right font-medium">
              {t(`plans.${preview.currentPlan}`)} · {t(`plans.${preview.currentInterval}`)}
              <span className="block text-xs text-ink-2 font-normal mt-0.5">
                {formatPeriodPrice(preview.currentPeriodPriceCents, preview.currentInterval, t, i18n.language)}
              </span>
            </span>
          </div>
          <div className="integrated-preview-row">
            <span className="text-ink-2">{t("integrated.newPlanLabel")}</span>
            <span className="text-right font-medium">
              {t(`plans.${preview.targetPlan}`)} · {t(`plans.${preview.targetInterval}`)}
              <span className="block text-xs text-ink-2 font-normal mt-0.5">
                {formatPeriodPrice(preview.targetPeriodPriceCents, preview.targetInterval, t, i18n.language)}
              </span>
            </span>
          </div>

          {annualToMonthlyPixBlocked && preview.availableFrom && preview.effectiveAt && (
            <div
              className="rounded-lg border border-warning/40 bg-warning/[0.06] px-3 py-2.5"
              role="alert"
            >
              <div className="text-sm font-semibold text-warning">{t("integrated.annualToMonthlyPixBlockedTitle")}</div>
              <p className="text-xs text-ink-2 mt-1 leading-relaxed">
                {t("integrated.annualToMonthlyPixBlockedBody", {
                  availableFrom: formatBillingDate(preview.availableFrom, i18n.language),
                  effectiveAt: formatBillingDate(preview.effectiveAt, i18n.language),
                })}
              </p>
            </div>
          )}

          {isImmediateUpgrade && !preview.isReactivation && !stone && (
            <div
              className="rounded-lg border border-[var(--purple-deep)]/35 bg-[var(--purple-deep)]/[0.07] px-3 py-2.5"
              role="status"
            >
              <div className="text-sm font-semibold text-[var(--purple-deep)]">
                {preview.targetPlan === "premium" && preview.currentPlan === "basic"
                  ? t("integrated.immediatePremiumTitle")
                  : t("integrated.immediateUpgradeTitle")}
              </div>
              <p className="text-xs text-ink-2 mt-1 leading-relaxed">
                {t("integrated.immediateUpgradeBody", { plan: t(`plans.${preview.targetPlan}`) })}
              </p>
            </div>
          )}

          {isImmediateUpgrade && preview.isReactivation && (
            <div
              className="rounded-lg border border-danger/30 bg-danger/[0.06] px-3 py-2.5"
              role="status"
            >
              <div className="text-sm font-semibold text-danger">{t("integrated.reactivationTitle")}</div>
              <p className="text-xs text-ink-2 mt-1 leading-relaxed">{t("integrated.reactivationBody")}</p>
              {preview.addOns && preview.addOns.length > 0 && (
                <p className="text-xs text-ink-2 mt-1 leading-relaxed">
                  {t("integrated.addOnsIncluded", {
                    items: preview.addOns
                      .map(
                        (addOn) =>
                          `${bonusLabel(addOn.key, addOn.name, t)} ${formatPeriodPrice(addOn.cents, preview.targetInterval, t, i18n.language)}`,
                      )
                      .join(", "),
                  })}
                </p>
              )}
            </div>
          )}

          {!crossInterval && preview.priceDiffCents !== 0 && (
            <div className="integrated-preview-row pt-1 border-t border-black/[0.06]">
              <span className="text-ink-2">{t("integrated.priceDifference")}</span>
              <span className={`font-medium ${preview.priceDiffCents > 0 ? "text-[var(--purple-deep)]" : "text-success"}`}>
                {preview.priceDiffCents > 0 ? "+" : "−"}
                {formatMoney(Math.abs(preview.priceDiffCents), i18n.language)}
              </span>
            </div>
          )}

          {showUpgradeBreakdown &&
            preview.unusedCreditCents > 0 &&
            (crossInterval || preview.chargeNowCents > 0) && (
            <div className="integrated-preview-row">
              <span className="text-ink-2">{t("integrated.unusedCredit")}</span>
              <span className="text-success font-medium">− {formatMoney(preview.unusedCreditCents, i18n.language)}</span>
            </div>
          )}

          {showUpgradeBreakdown && !crossInterval && preview.coveredDays != null && preview.coveredDays > 0 && !preview.isReactivation && (
            <div className="integrated-preview-row">
              <span className="text-ink-2">{t("integrated.coveredDays")}</span>
              <span className="font-medium">
                {t("integrated.coveredDaysValue", { days: String(Math.ceil(preview.coveredDays)) })}
              </span>
            </div>
          )}

          {stone && isImmediateUpgrade && (
            <div className="space-y-2 pt-2 border-t border-black/[0.06]">
              <div className="text-sm font-semibold">{t("integrated.stoneChangeTitle")}</div>
              <p className="text-xs text-ink-2 leading-relaxed">
                {t("integrated.stoneChangeFirstYear", {
                  total: formatMoney(stone.installments.first_year_cents, i18n.language),
                })}
              </p>
              <p className="text-xs text-ink-2 leading-relaxed">
                {t("integrated.stoneChangeRenewal", {
                  count: stone.installments.count,
                  installment: formatMoney(stone.installments.renewal_installment_cents, i18n.language),
                })}
              </p>
              <p className="text-xs text-ink-2 leading-relaxed">{t("integrated.stoneChangeOldStops")}</p>
              {stone.inRefundWindow && (
                <p className="text-xs text-ink-2 leading-relaxed">{t("integrated.cheaperPlanAnnualRefundNote")}</p>
              )}
              <StoneCardForm value={stone.card} onChange={stone.onCardChange} disabled={submitting} />
            </div>
          )}

          {stone ? null : raiseInPlaceHint ? (
            <p className="text-xs text-ink-2 pt-1">{raiseInPlaceHint}</p>
          ) : preview.requiresProviderApproval && preview.firstChargeAt && (
            <div className="integrated-preview-row pt-1 border-t border-black/[0.06]">
              <span className="text-ink-2">{t("integrated.firstCharge")}</span>
              <span className="font-medium text-right">
                {formatBillingDate(preview.firstChargeAt, i18n.language)}
                <span className="block text-xs text-ink-2 font-normal mt-0.5">
                  {formatPeriodPrice(preview.recurringPriceCents, preview.targetInterval, t, i18n.language)}
                </span>
              </span>
            </div>
          )}

          {!stone && !raiseInPlaceHint && preview.requiresProviderApproval && (
            <p className="text-xs text-ink-2 pt-1">
              {t(
                preview.chargeNowCents > 0
                  ? "integrated.approveRecurringHintWithCharge"
                  : "integrated.approveRecurringHint",
              )}
            </p>
          )}

          {showUpgradeBreakdown && !crossInterval && prorationCents > 0 && preview.priceDiffCents !== prorationCents && (
            <div className="integrated-preview-row">
              <span className="text-ink-2">{t("integrated.proratedDifference")}</span>
              <span className="font-medium">{formatMoney(prorationCents, i18n.language)}</span>
            </div>
          )}

          {breakdown && breakdown.discountCents > 0 && (
            <div className="integrated-preview-row">
              <span className="text-ink-2">{t("checkout.discount")}</span>
              <span className="text-success font-medium">− {formatMoney(breakdown.discountCents, i18n.language)}</span>
            </div>
          )}

          {isScheduledChange && preview.effectiveAt && (
            <div className="integrated-preview-row pt-1 border-t border-black/[0.06]">
              <span className="text-ink-2">{t("integrated.scheduledFor")}</span>
              <span className="font-medium">{formatBillingDate(preview.effectiveAt, i18n.language)}</span>
            </div>
          )}
        </div>
      )}

      <div className="border-t border-black/10 pt-4 flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-ink-2">
            {isScheduledChange ? t("integrated.noPaymentNow") : t("integrated.dueToday")}
          </div>
          <div className="integrated-due-today mt-0.5">{dueTodayLabel}</div>
        </div>
      </div>

      {pricingError && (
        <p className="text-sm text-danger">
          {pricingError === "contact_support" ? t("integrated.changeContactSupport") : t("common.error")}
        </p>
      )}

      {showConfirm && (
        <button
          type="button"
          className="btn-primary w-full"
          disabled={
            selectionBlocksChange ||
            submitting ||
            pricingLoading ||
            annualToMonthlyPixBlocked ||
            (dueTodayLabel === "—" && !hasActivePendingPix)
          }
          onClick={onConfirm}
        >
          {submitting
            ? t("integrated.processing")
            : annualToMonthlyPixBlocked
              ? t("integrated.annualToMonthlyPixBlockedCta")
              : selectionBlocksChange
                ? t("integrated.noChanges")
                : confirmLabel}
        </button>
      )}

      {changeError && <p className="text-sm text-danger">{changeError}</p>}
      {changeError && onPayByCard && (
        <button type="button" className="btn-ghost text-sm px-3 py-1.5" onClick={onPayByCard}>
          {t("checkout.pixUnavailableCta")}
        </button>
      )}
    </div>
  );
}

function CpfCheckoutDialog({
  initialCpf,
  submitting,
  error,
  forCard = false,
  onClose,
  onConfirm,
}: {
  initialCpf: string;
  /** The 12x on Stone also needs a valid CPF: the dialog speaks of the card, not of PIX. */
  forCard?: boolean;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (cpf: string) => void;
}) {
  const { t } = useTranslation();
  const [cpf, setCpf] = useState(initialCpf);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="card-modal max-w-md w-full p-6 space-y-4 text-[color:var(--text-primary)]">
        <div className="text-lg font-semibold">{t("integrated.cpfDialogTitle")}</div>
        <p className="text-sm text-ink-2 leading-relaxed">
          {t(forCard ? "integrated.cpfDialogCardBody" : "integrated.cpfDialogRetryBody")}
        </p>
        <label className="block">
          <span className="field-label">{t("checkout.cpf")}</span>
          <CpfInput
            placeholder={t("checkout.cpfPlaceholder")}
            value={cpf}
            onChange={setCpf}
            required
            autoComplete="off"
          />
        </label>
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" className="btn-ghost" disabled={submitting} onClick={onClose}>
            {t("common.back")}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={submitting}
            onClick={() => onConfirm(cpf)}
          >
            {submitting
              ? t("integrated.processing")
              : t(forCard ? "integrated.cpfDialogCardConfirm" : "integrated.cpfDialogConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

function BillingSupportWhatsAppLink({
  href,
  ...props
}: React.ComponentPropsWithoutRef<"a"> & { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline underline-offset-2 hover:opacity-100"
      {...props}
    />
  );
}

function PendingScheduleBlocksChangeMessage({
  className,
  whatsappUrl,
}: {
  className?: string;
  whatsappUrl: string;
}) {
  return (
    <p className={className}>
      <Trans
        i18nKey="integrated.pendingScheduleBlocksChange"
        components={{ whatsapp: <BillingSupportWhatsAppLink href={whatsappUrl} /> }}
      />
    </p>
  );
}

function ScheduledChangeSupportDialog({
  change,
  current,
  reason,
  email,
  requestedChangeDetails,
  onClose,
}: {
  change: SessionResponse["scheduled_changes"][number];
  current: SessionResponse["local_subscription"];
  reason: "cancel" | "replace" | "reverse_openpix_cancellation";
  email: string;
  requestedChangeDetails?: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const copy = getScheduledChangeSupportCopy(change, current, reason, t, i18n.language);
  const whatsappUrl = buildBillingSupportWhatsAppUrl(t, {
    email,
    intent:
      reason === "replace"
        ? "replace_scheduled_change"
        : reason === "reverse_openpix_cancellation"
          ? "reverse_openpix_cancellation"
          : "cancel_scheduled_change",
    scheduledChangeDetails: describeScheduledChange(change, t, i18n.language),
    requestedChangeDetails,
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="card-modal max-w-md w-full p-6 space-y-4 text-[color:var(--text-primary)]">
        <div className="text-lg font-semibold">{copy.title}</div>
        <p className="text-sm text-ink-2 leading-relaxed">{copy.body}</p>
        <p className="text-xs text-ink-2 rounded-lg bg-black/[0.04] px-3 py-2.5">
          {describeScheduledChange(change, t, i18n.language)}
        </p>
        <p className="text-sm">
          <span className="text-ink-2">{t("integrated.supportWhatsAppNumber")}: </span>
          <span className="font-medium">{BILLING_SUPPORT_WHATSAPP_DISPLAY}</span>
        </p>
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t("common.back")}
          </button>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            {t("integrated.contactSupportWhatsApp")}
          </a>
        </div>
      </div>
    </div>
  );
}

function AlreadyScheduledChangeDialog({
  change,
  onClose,
}: {
  change: SessionResponse["scheduled_changes"][number];
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="card-modal max-w-md w-full p-6 space-y-4 text-[color:var(--text-primary)]">
        <div className="text-lg font-semibold">{t("integrated.alreadyScheduledTitle")}</div>
        <p className="text-sm text-ink-2 leading-relaxed">{t("integrated.alreadyScheduledBody")}</p>
        <p className="text-xs text-ink-2 rounded-lg bg-black/[0.04] px-3 py-2.5">
          {describeScheduledChange(change, t, i18n.language)}
        </p>
        <div className="flex justify-end pt-1">
          <button type="button" className="btn-primary" onClick={onClose}>
            {t("integrated.alreadyScheduledOk")}
          </button>
        </div>
      </div>
    </div>
  );
}

function KeepPremiumRaiseDialog({
  change,
  amountLabel,
  submitting,
  error,
  onClose,
  onConfirm,
}: {
  change: SessionResponse["scheduled_changes"][number];
  amountLabel: string;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const date = formatBillingDate(change.effectiveAt, i18n.language);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="card-modal max-w-md w-full p-6 space-y-4 text-[color:var(--text-primary)]">
        <div className="text-lg font-semibold">{t("integrated.keepPremiumConfirmTitle")}</div>
        <p className="text-sm text-ink-2 leading-relaxed">
          {t("integrated.keepPremiumConfirmBody", { date, amount: amountLabel })}
        </p>
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={submitting}>
            {t("common.back")}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={submitting}
            onClick={() => void onConfirm()}
          >
            {submitting ? t("integrated.processing") : t("integrated.keepPremium")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReverseScheduledCancellationDialog({
  token,
  change,
  current,
  onClose,
  onDone,
}: {
  token: string;
  change: SessionResponse["scheduled_changes"][number];
  current: SessionResponse["local_subscription"];
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const planLabel = current ? t(`plans.${current.plan}`) : "";
  const intervalLabel = current ? t(`plans.${current.interval}`) : "";
  const date = formatBillingDate(change.effectiveAt, i18n.language);

  async function confirm() {
    setSubmitting(true);
    setError(null);
    try {
      await api(`/api/integrated/scheduled-changes/${change.id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      await onDone();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(t("integrated.reverseCancellationContactSupport"));
      } else {
        setError(t("integrated.changeFailed"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="card-modal max-w-md w-full p-6 space-y-4 text-[color:var(--text-primary)]">
        <div className="text-lg font-semibold">{t("integrated.cancelScheduledChangeTitle")}</div>
        <p className="text-sm text-ink-2 leading-relaxed">
          {t("integrated.cancelScheduledBodyCancellation", {
            date,
            currentPlan: planLabel,
            currentInterval: intervalLabel,
          })}
        </p>
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={submitting}>
            {t("common.back")}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={submitting}
            onClick={() => void confirm()}
          >
            {submitting ? t("integrated.processing") : t("integrated.reverseCancellationConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** reason → rescue (only when the reason has one) → confirm (explanation + CANCELAR) → done. */
type CancelStage =
  | "loading"
  | "manage_booking"
  | "reason"
  | "ladder"
  | "rescue"
  | "slots"
  | "reschedule"
  | "booked"
  | "confirm"
  | "done";

interface SupportBookingInfo {
  id: string;
  starts_at: string;
  ends_at: string;
  status: string;
  reason?: string;
  source?: string;
  slot_id?: string;
}

function CancelDialog({
  token,
  email,
  intent = "flow",
  refundOffer,
  periodEnd,
  cheaperPlanCurrent,
  planChange,
  stoneChange = false,
  appLanguage,
  money,
  onPickCheaperPlan,
  onClose,
  onDone,
  onBookingChange,
  onRefundWindowClosed,
  subscriptionOver = false,
}: {
  token: string;
  email: string;
  intent?: "flow" | "reschedule";
  /** The subscription the cheaper-plan card prices against (PIT-452); null offers nothing. */
  cheaperPlanCurrent: CheaperPlanCurrent | null;
  /** Where "Quero trocar de plano" leads (PIT-546 f): the plans, a reactivation, or support. */
  planChange: PlanChangePath;
  /** The session offers the annual 12x on Stone: the card prices it too. */
  stoneChange?: boolean;
  appLanguage?: string;
  /** Money in the page's currency, as the plan picker shows it. */
  money: (cents: number) => string;
  /** A plan picked from the cheaper-plan card, or under "Quero trocar de plano": the page takes it into its plan picker. */
  /** `withGift`: picked from the offer that brings the Zenlinks gift with the plan. */
  onPickCheaperPlan: (target: CheaperPlanTarget, withGift: boolean) => void;
  /** Inside the 7-day window the confirm screen offers two ways out; null outside it. */
  refundOffer: CancellationRefundOffer | null;
  /** Last day of access when the renewal stops. */
  periodEnd: string | null;
  onClose: () => void;
  onDone: () => void | Promise<void>;
  onBookingChange?: (booking: SupportBookingInfo | null) => void;
  /** The window closed between showing the choice and confirming it; reload so it disappears. */
  onRefundWindowClosed?: () => void | Promise<void>;
  /**
   * The subscription is already over (PIT-546 g): confirming only stops what the provider may still
   * charge, so no gift, no help screens and no list of what access ends.
   */
  subscriptionOver?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";
  const [phrase, setPhrase] = useState("");
  const [stage, setStage] = useState<CancelStage>("loading");
  const [booking, setBooking] = useState<SupportBookingInfo | null>(null);
  const [slots, setSlots] = useState<SupportSlot[]>([]);
  const [pendingSlot, setPendingSlot] = useState<SupportSlot | null>(null);
  const [reason, setReason] = useState<CancellationReason | null>(null);
  const [subreasons, setSubreasons] = useState<string[]>([]);
  // "Quero trocar de plano" (PIT-546 f): the plan asked for, shown in the picker on "Ver esse plano";
  // where the page cannot make the change, support on WhatsApp. Never the plain "Continuar".
  const planTargets = planChange.kind === "support" ? [] : planChange.targets;
  const [planTarget, setPlanTarget] = useState<CheaperPlanTarget | null>(null);
  const reasonAction = reasonStepAction(reason, planChange);
  const [firstPublicationCompleted, setFirstPublicationCompleted] = useState(true);
  // Zenpost lets this customer book a session (the flag behind should_offer_support_before_cancel).
  const [canBookSupport, setCanBookSupport] = useState(false);
  // Slots that keep the refund reachable, loaded on the confirm screen to decide on the session card
  // (PIT-448); null until loaded or when the card cannot show.
  const [refundWindowSlots, setRefundWindowSlots] = useState<SupportSlot[] | null>(null);
  // Where "Voltar" on the slots goes: the help screen, or the confirm screen when the card opened them.
  const [slotsReturnStage, setSlotsReturnStage] = useState<"rescue" | "confirm">("rescue");
  const specialistCardViewedRef = useRef(false);
  // What the confirm screen lists as lost; null (no list) when the context could not be loaded.
  const [lossContext, setLossContext] = useState<{
    bonuses: CancelRescueBonus[] | null;
    zenlinksPage: CancelRescueZenlinksPage | null;
    assets: CancelRescueAssets | null;
  } | null>(null);
  // The Zenlinks for some months instead of cancelling (PIT-54), when Zenpost offers it.
  const [retentionOffer, setRetentionOffer] = useState<RetentionOffer | null>(null);
  // The retention ladder (shared/retention_ladder.ts): what Zenpost offers, null when it is off.
  const [ladderOffers, setLadderOffers] = useState<RetentionLadderOffers | null>(null);
  // Its screens, fixed when it opens; once it ran, the confirm screen offers nothing it already did.
  const [ladderSteps, setLadderSteps] = useState<RetentionLadderStep[]>([]);
  const [ladderRan, setLadderRan] = useState(false);
  // The experiment's attempt (server/retention_attempts.ts), sent with every answer; null with the
  // ladder flag off. Paused: an offer was shown within the cooldown, so this dialog offers nothing.
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [offersPaused, setOffersPaused] = useState(false);
  const [giftState, setGiftState] = useState<RetentionGiftState>("idle");
  const [giftUntil, setGiftUntil] = useState<string | null>(null);
  const [giftError, setGiftError] = useState<string | null>(null);
  const giftOfferedRef = useRef(false);
  // The cheaper plans priced for a price reason (PIT-452); null until priced.
  const [cheaperOffers, setCheaperOffers] = useState<ChangePreview[] | null>(null);
  /** The 12x figures of the annual option, from the same /pricing answer. */
  const [cheaperStone, setCheaperStone] = useState<StoneInstallments | null>(null);
  const cheaperOfferedRef = useRef(false);
  const [details, setDetails] = useState("");
  const [date, setDate] = useState<string | null>(null);
  const [refundAmountLabel, setRefundAmountLabel] = useState<string | null>(null);
  // "Stop the renewal" comes pre-selected; the refund is one tap away.
  const [choice, setChoice] = useState<CancellationChoice>("stop_renewal");
  // The offer as it stood when the renewal was stopped, for the final screen.
  const [stoppedWithOffer, setStoppedWithOffer] = useState<CancellationRefundOffer | null>(null);
  // An ended subscription's cancel: whether the provider is known to charge nothing more now.
  const [expiredStopConfirmed, setExpiredStopConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmCancelBooking, setConfirmCancelBooking] = useState(false);

  const postEvent = useCallback(
    (event_type: string, metadata?: Record<string, unknown>) => {
      void api("/api/integrated/cancel-rescue/events", {
        method: "POST",
        body: JSON.stringify({ token, event_type, metadata, retention_attempt_id: attemptId ?? undefined }),
      }).catch(() => undefined);
    },
    [token, attemptId],
  );

  const loadSlots = useCallback(async () => {
    const list = await api<SupportSlot[]>(
      `/api/integrated/cancel-rescue/slots?token=${encodeURIComponent(token)}`,
    );
    setSlots(list);
    return list;
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ctx = await api<{
          first_publication_completed: boolean;
          should_offer_support_before_cancel: boolean;
          active_support_booking: SupportBookingInfo | null;
          bonuses?: CancelRescueBonus[];
          zenlinks_page?: CancelRescueZenlinksPage | null;
          assets?: CancelRescueAssets | null;
          retention_offer?: RetentionOffer | null;
          retention_ladder?: RetentionLadderOffers | null;
          retention_attempt_id?: string | null;
          offers_paused?: boolean;
        }>(
          // purpose=cancel makes this dialog an attempt of the experiment; rescheduling is none, and
          // so is an ended subscription's cancel, which offers nothing (PIT-546 g).
          `/api/integrated/cancel-rescue?token=${encodeURIComponent(token)}${intent === "reschedule" || subscriptionOver ? "" : "&purpose=cancel"}`,
        );
        if (cancelled) return;
        setFirstPublicationCompleted(ctx.first_publication_completed !== false);
        setCanBookSupport(ctx.should_offer_support_before_cancel === true);
        setLossContext({
          bonuses: ctx.bonuses ?? null,
          zenlinksPage: ctx.zenlinks_page ?? null,
          assets: ctx.assets ?? null,
        });
        setRetentionOffer(subscriptionOver ? null : (ctx.retention_offer ?? null));
        setLadderOffers(subscriptionOver ? null : (ctx.retention_ladder ?? null));
        setAttemptId(ctx.retention_attempt_id ?? null);
        setOffersPaused(ctx.offers_paused === true);
        if (ctx.active_support_booking) {
          setBooking(ctx.active_support_booking);
          if (intent === "reschedule") {
            try {
              await loadSlots();
              if (!cancelled) setStage("reschedule");
            } catch {
              if (!cancelled) {
                setError(t("integrated.cancelErrorGeneric"));
                setStage("manage_booking");
              }
            }
            return;
          }
          setStage("manage_booking");
          return;
        }
        // The reason comes first so the help offered after it can match it.
        setStage("reason");
      } catch {
        // Soft-fail: cancel isn't blocked by API blips.
        if (!cancelled) setStage("reason");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, intent, loadSlots, t]);

  const rescueKind = reason && !subscriptionOver ? rescueForReason(reason) : "none";
  const rescueMetadata = reason
    ? {
        cancellation_reason: reason,
        rescue_kind: rescueKind,
        ...(subreasons.length > 0 ? { cancellation_subreasons: subreasons.join(",") } : {}),
      }
    : undefined;

  function describeReason(): string {
    if (!reason) return "";
    const picked = subreasons.map((s) => t(`integrated.cancelSubreason_${reason}_${s}`));
    const label = t(`integrated.cancelReason_${reason}`);
    return picked.length > 0 ? `${label} (${picked.join(", ")})` : label;
  }

  const rescueWhatsAppUrl = buildBillingSupportWhatsAppUrl(t, {
    email,
    intent: "cancel_rescue",
    cancellationReasonDetails: describeReason() || undefined,
  });

  function continueFromReason() {
    if (!reason) {
      setError(t("integrated.cancelReasonRequired"));
      return;
    }
    setError(null);
    // The ladder, when on, replaces the help screen: its last step is the specialist. Within the
    // cooldown after an offer it carries the specialist alone (owner, 21/10). An ended subscription
    // gets none of it: its cancel only stops what the provider may still charge (PIT-546 g).
    const steps = subscriptionOver
      ? []
      : retentionLadderSteps({
          offers: ladderOffers,
          refundableUntil: refundOffer ? new Date(refundOffer.refundable_until) : null,
          now: new Date(),
          reason,
        });
    if (steps.length > 0) {
      setLadderSteps(steps);
      setStage("ladder");
      return;
    }
    // Within the cooldown, nothing else: straight to the confirm screen (owner, 20/10).
    if (subscriptionOver || offersPaused || rescueForReason(reason) === "none") {
      setStage("confirm");
      return;
    }
    setStage("rescue");
    postEvent("cancel_rescue_viewed", rescueMetadata);
  }

  function openRescueWhatsApp() {
    postEvent("cancel_rescue_whatsapp_opened", rescueMetadata);
  }

  // "Quero trocar de plano" where support makes the change: the message asks for the change, not
  // for help before cancelling.
  const planSupportWhatsAppUrl = buildBillingSupportWhatsAppUrl(t, { email, intent: "change_plan" });

  /** Counted as the WhatsApp help it is; `change_plan` has no help screen, so its kind is "none". */
  function openPlanSupportWhatsApp() {
    postEvent("cancel_rescue_whatsapp_opened", { ...rescueMetadata, rescue_kind: "whatsapp_now" });
  }

  function declineRescue() {
    postEvent("cancel_rescue_declined", rescueMetadata);
    setError(null);
    setStage("confirm");
  }

  async function openSlots(next: "slots" | "reschedule", returnTo: "rescue" | "confirm" = "rescue") {
    setError(null);
    setSubmitting(true);
    try {
      await loadSlots();
      setSlotsReturnStage(returnTo);
      setStage(next);
    } catch {
      // The rescue screen still offers WhatsApp and "continue cancelling", so stay put.
      setError(t("integrated.cancelErrorGeneric"));
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmSlot() {
    if (!pendingSlot) return;
    setSubmitting(true);
    setError(null);
    try {
      if (stage === "reschedule" && booking) {
        const updated = await api<SupportBookingInfo>(
          `/api/integrated/cancel-rescue/bookings/${booking.id}/reschedule`,
          {
            method: "POST",
            body: JSON.stringify({ token, slot_id: pendingSlot.id }),
          },
        );
        setBooking(updated);
        setPendingSlot(null);
        onBookingChange?.(updated);
        if (intent === "reschedule") {
          onClose();
          return;
        }
        setStage("manage_booking");
      } else {
        const created = await api<SupportBookingInfo>("/api/integrated/cancel-rescue/bookings", {
          method: "POST",
          body: JSON.stringify({
            token,
            slot_id: pendingSlot.id,
            customer_notes: describeReason() || undefined,
            rescue_kind: slotsReturnStage === "confirm" ? REFUND_WINDOW_SPECIALIST_KIND : rescueKind,
            retention_attempt_id: attemptId ?? undefined,
          }),
        });
        setBooking(created);
        setPendingSlot(null);
        onBookingChange?.(created);
        setStage("booked");
      }
    } catch (err) {
      if (err instanceof ApiError && apiErrorCode(err) === "slot_after_refund_window") {
        // The window moved on while the slots were open: offer what still fits.
        setPendingSlot(null);
        setError(t("integrated.cancelSlotAfterRefundWindow"));
        await loadSlots().catch(() => undefined);
        return;
      }
      setError(t("integrated.cancelErrorGeneric"));
    } finally {
      setSubmitting(false);
    }
  }

  async function cancelBooking() {
    if (!booking) return;
    setSubmitting(true);
    setError(null);
    try {
      await api(`/api/integrated/cancel-rescue/bookings/${booking.id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      setBooking(null);
      setConfirmCancelBooking(false);
      onBookingChange?.(null);
      setStage("reason");
    } catch {
      setError(t("integrated.cancelErrorGeneric"));
    } finally {
      setSubmitting(false);
    }
  }

  // Outside the window there is one way out, and the request says so: what happens is what the
  // screen showed, even if the server would still have refunded.
  const effectiveChoice: CancellationChoice = refundOffer ? choice : "stop_renewal";
  const periodEndLabel = periodEnd ? formatBillingDate(periodEnd, i18n.language) : "";
  const offerAmountLabel = refundOffer ? formatRefundMoney(refundOffer.amount_cents, refundOffer, i18n.language) : "";
  const offerDeadlineLabel = refundOffer ? formatRefundDeadline(refundOffer.refundable_until, t, i18n.language) : "";
  const losses = lossContext && !subscriptionOver
    ? buildCancellationLosses({ ...lossContext, choice: effectiveChoice, periodEndLabel, t })
    : null;

  const showGift =
    stage === "confirm" &&
    !ladderRan &&
    !offersPaused &&
    showsRetentionGift({
      reason,
      offer: retentionOffer,
      state: giftState,
      cheaperPlanOptions: offersCheaperPlan(reason) ? (cheaperOffers?.length ?? null) : 0,
    });

  // Counted once, when the card is first on screen (PIT-54).
  useEffect(() => {
    if (!showGift || giftOfferedRef.current || !reason) return;
    giftOfferedRef.current = true;
    postEvent("cancel_rescue_bonus_offered", { cancellation_reason: reason });
  }, [showGift, reason, postEvent]);

  // The cheaper plan for price (PIT-452), priced through the plan picker's own preview so the card
  // shows what the change flow will charge. An option that fails to price is left out.
  const wantsCheaperPlan = stage === "confirm" && !offersPaused && offersCheaperPlan(reason);
  useEffect(() => {
    if (!wantsCheaperPlan || cheaperOffers !== null) return;
    const targets = cheaperPlanTargets(cheaperPlanCurrent, { stoneChange });
    if (targets.length === 0 || !cheaperPlanCurrent) {
      setCheaperOffers([]);
      return;
    }
    let cancelled = false;
    void Promise.allSettled(
      targets.map((target) =>
        api<{ change_preview: ChangePreview | null; stone_installments?: StoneInstallments | null }>(
          "/api/integrated/pricing",
          {
            method: "POST",
            body: JSON.stringify({
              token,
              target_plan: target.plan,
              target_interval: target.interval,
              target_payment_method: target.paymentMethod ?? cheaperPlanCurrent.paymentMethod,
              app_language: appLanguage,
            }),
          },
        ),
      ),
    ).then((results) => {
      if (cancelled) return;
      let stone: StoneInstallments | null = null;
      const priced = results.flatMap((r) => {
        if (r.status !== "fulfilled" || !isCheaperPlanOffer(r.value.change_preview)) return [];
        // The annual is offered only in 12x: one priced without it (the server read the card as
        // Stripe, paid at once) is left out.
        if (isInstallmentOffer(r.value.change_preview)) {
          if (!r.value.stone_installments) return [];
          stone = r.value.stone_installments;
        }
        return [r.value.change_preview];
      });
      setCheaperStone(stone);
      setCheaperOffers(priced);
    });
    return () => {
      cancelled = true;
    };
  }, [wantsCheaperPlan, cheaperOffers, cheaperPlanCurrent, stoneChange, token, appLanguage]);

  const showCheaper =
    stage === "confirm" &&
    !offersPaused &&
    giftState !== "accepted" &&
    showsCheaperPlanOffer({ reason, offers: cheaperOffers ?? [] });

  // Counted once, when the card is first on screen.
  useEffect(() => {
    if (!showCheaper || cheaperOfferedRef.current || !reason || !cheaperOffers) return;
    cheaperOfferedRef.current = true;
    postEvent("cancel_rescue_plan_offered", {
      cancellation_reason: reason,
      options: cheaperOffers.map((p) => `${p.targetPlan}_${p.targetInterval}`).join(","),
    });
  }, [showCheaper, reason, cheaperOffers, postEvent]);

  /** "Ver esse plano": the picker on the plan asked for; nothing changes until it is confirmed there. */
  function goToPlanPicker() {
    if (!planTarget) return;
    pickCheaperPlan(planTarget, false);
  }

  function pickCheaperPlan(target: CheaperPlanTarget, withGift: boolean) {
    postEvent("cancel_rescue_plan_accepted", {
      cancellation_reason: reason,
      target_plan: target.plan,
      target_interval: target.interval,
      with_gift: withGift,
      // A plan to come back on, on a subscription that is over: the picker takes it as a reactivation.
      ...(leadsToPlanPicker(reason) && planChange.kind === "reactivate" ? { reactivation: true } : {}),
    });
    onPickCheaperPlan(target, withGift);
  }

  // The session card inside the refund window (PIT-448). The schedule is read only for someone who
  // could see it, and the card shows only with a slot that keeps the refund reachable.
  const specialistCardArgs = {
    inRefundWindow: refundOffer != null,
    canBookSupport,
    // A cheaper plan still being priced counts as shown, so the session card never flashes first.
    otherOfferShown: showGift || showCheaper || (wantsCheaperPlan && cheaperOffers === null),
    hasBooking: booking != null,
    rescueKind,
  };
  const mayShowSpecialist =
    stage === "confirm" &&
    !ladderRan &&
    !offersPaused &&
    giftState !== "accepted" &&
    mayShowRefundWindowSpecialist(specialistCardArgs);
  const showSpecialist =
    mayShowSpecialist &&
    showsRefundWindowSpecialist({
      ...specialistCardArgs,
      slotsBeforeDeadline: refundWindowSlots?.length ?? 0,
    });

  useEffect(() => {
    if (!mayShowSpecialist || refundWindowSlots !== null) return;
    let cancelled = false;
    loadSlots()
      .then((list) => {
        if (!cancelled) setRefundWindowSlots(list);
      })
      .catch(() => {
        // No schedule, no card: the screen is as before.
        if (!cancelled) setRefundWindowSlots([]);
      });
    return () => {
      cancelled = true;
    };
  }, [mayShowSpecialist, refundWindowSlots, loadSlots]);

  // Counted once, when the card is first on screen.
  useEffect(() => {
    if (!showSpecialist || specialistCardViewedRef.current || !reason) return;
    specialistCardViewedRef.current = true;
    postEvent("cancel_rescue_viewed", {
      cancellation_reason: reason,
      rescue_kind: REFUND_WINDOW_SPECIALIST_KIND,
    });
  }, [showSpecialist, reason, postEvent]);

  async function acceptGift() {
    if (!reason) return;
    setGiftState("accepting");
    setGiftError(null);
    try {
      const grant = await api<{ key: string; expires_at: string }>(
        "/api/integrated/cancel-rescue/retention-bonus",
        {
          method: "POST",
          body: JSON.stringify({ token, cancellation_reason: reason, retention_attempt_id: attemptId ?? undefined }),
        },
      );
      setGiftUntil(formatBillingDate(grant.expires_at, i18n.language));
      setGiftState("accepted");
    } catch (err) {
      // Zenpost no longer offers it (given meanwhile, or the customer has it): the card goes and
      // the screen is as before. Anything else keeps the card with a retry.
      if (err instanceof ApiError && err.status === 409) {
        setGiftState("unavailable");
        return;
      }
      setGiftState("idle");
      setGiftError(t("integrated.retentionGiftError"));
    }
  }

  async function confirmCancel() {
    if (!reason) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await api<{
        /** Null for a subscription that was already over: only its provider recurrence was stopped. */
        effective_at: string | null;
        refunded?: boolean;
        refund_amount_cents?: number;
        cancellation_refund?: CancellationRefundOffer | null;
        provider_stopped?: string;
      }>("/api/integrated/cancel", {
        method: "POST",
        body: JSON.stringify({
          token,
          confirmation_phrase: phrase,
          cancellation_reason: reason,
          cancellation_subreasons: subreasons.length > 0 ? subreasons : undefined,
          cancellation_details: details.trim() || undefined,
          cancellation_choice: effectiveChoice,
          retention_attempt_id: attemptId ?? undefined,
        }),
      });
      setDate(r.effective_at ? formatBillingDate(r.effective_at, i18n.language) : null);
      setExpiredStopConfirmed(["ok", "already_gone", "not_billing"].includes(r.provider_stopped ?? ""));
      setRefundAmountLabel(
        r.refunded && typeof r.refund_amount_cents === "number"
          ? formatRefundMoney(r.refund_amount_cents, refundOffer, i18n.language)
          : null,
      );
      // The server's answer, not the offer loaded with the page: a tab left open past the deadline
      // must not promise a refund that is gone.
      setStoppedWithOffer(r.refunded ? null : (r.cancellation_refund ?? null));
      setStage("done");
      if (specialistCardViewedRef.current) {
        // Saw the session card and cancelled without booking (PIT-448).
        postEvent("cancel_rescue_declined", {
          cancellation_reason: reason,
          rescue_kind: REFUND_WINDOW_SPECIALIST_KIND,
          cancellation_choice: effectiveChoice,
        });
      }
      await onDone();
    } catch (err) {
      const code = err instanceof ApiError ? apiErrorCode(err) : undefined;
      if (code === "refund_not_eligible") {
        setChoice("stop_renewal");
        // Nothing was cancelled: the renewal is still on until they confirm again.
        setError(t("integrated.cancelRefundWindowClosedNotCancelled", { date: periodEndLabel }));
        await onRefundWindowClosed?.();
        return;
      }
      setError(
        code === "support_booking_active"
          ? t("integrated.cancelBookingBlocked")
          : code === "stone_change_pending"
            ? t("integrated.cancelStoneChangePending")
            : t("integrated.changeFailed"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const days = groupSupportSlotsByDay(slots, locale);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="card-modal max-w-md w-full p-6 space-y-4 text-[color:var(--text-primary)] max-h-[90vh] overflow-y-auto relative">
        {stage !== "done" && (
          <button
            type="button"
            className="absolute top-4 right-4 p-1 rounded-md text-ink-2 hover:text-[color:var(--text-primary)] transition-colors"
            onClick={onClose}
            disabled={submitting}
            aria-label={t("common.close")}
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {stage === "loading" && (
          <div className="text-sm text-ink-2 pr-8">{t("integrated.cancelBusy")}</div>
        )}

        {stage === "manage_booking" && booking && (
          <>
            <div className="text-lg font-semibold pr-8">{t("integrated.cancelBookedTitle")}</div>
            <p className="text-sm text-ink-2">{t("integrated.cancelBookedBody")}</p>
            <SupportBookingCard
              booking={booking}
              locale={locale}
              title={t("integrated.cancelBookingCardTitle")}
              body={t("integrated.cancelBookingCardBody")}
              rescheduleLabel={t("integrated.cancelBookingCardReschedule")}
              cancelLabel={t("integrated.cancelBookingCardCancel")}
              busy={submitting}
              onReschedule={() => void openSlots("reschedule")}
              onCancel={() => setConfirmCancelBooking(true)}
            />
            {error && <p className="text-sm text-danger">{error}</p>}
            {confirmCancelBooking && (
              <div className="space-y-3 rounded-xl border border-[color:var(--border)] p-4">
                <p className="text-sm">{t("integrated.cancelManageCancelBookingConfirm")}</p>
                <div className="flex gap-2 justify-end flex-wrap">
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={submitting}
                    onClick={() => setConfirmCancelBooking(false)}
                  >
                    {t("integrated.cancelManageKeep")}
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={submitting}
                    onClick={() => void cancelBooking()}
                  >
                    {submitting ? t("integrated.cancelBusy") : t("integrated.cancelManageCancelBookingYes")}
                  </button>
                </div>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn-primary" onClick={onClose} disabled={submitting}>
                {t("integrated.cancelBookedClose")}
              </button>
            </div>
          </>
        )}

        {stage === "ladder" && reason && ladderOffers && (
          <RetentionLadder
            token={token}
            attemptId={attemptId}
            steps={ladderSteps}
            offers={ladderOffers}
            assets={lossContext?.assets ?? null}
            reason={reason}
            reasonLabel={describeReason()}
            refundableUntil={refundOffer?.refundable_until ?? null}
            onContinueCancelling={() => {
              setLadderRan(true);
              setStage("confirm");
            }}
            onClose={onClose}
          />
        )}

        {stage === "rescue" && reason && rescueKind !== "none" && (
          <>
            <div className="text-lg font-semibold pr-8">
              {rescueKind === "whatsapp_now"
                ? t("integrated.cancelRescueWhatsAppTitle")
                : t("integrated.cancelRescueSpecialistTitle")}
            </div>
            <p className="text-sm text-ink-2">
              {rescueKind === "whatsapp_now"
                ? t("integrated.cancelRescueWhatsAppBody")
                : t("integrated.cancelRescueSpecialistBody")}
            </p>
            {rescueKind === "whatsapp_now" && !firstPublicationCompleted && (
              <p className="text-sm font-medium">{t("integrated.cancelRescueFirstPostHint")}</p>
            )}
            <div className="flex flex-col gap-2">
              {rescueKind === "whatsapp_now" ? (
                <>
                  <a
                    className="btn-primary w-full text-center"
                    href={rescueWhatsAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={openRescueWhatsApp}
                  >
                    {t("integrated.cancelRescueWhatsAppCta")}
                  </a>
                  <button
                    type="button"
                    className="btn-ghost w-full"
                    disabled={submitting}
                    onClick={() => void openSlots("slots")}
                  >
                    {submitting ? t("integrated.cancelBusy") : t("integrated.cancelRescueBookInstead")}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-primary w-full"
                    disabled={submitting}
                    onClick={() => void openSlots("slots")}
                  >
                    {submitting ? t("integrated.cancelBusy") : t("integrated.cancelRescueSpecialistCta")}
                  </button>
                  <a
                    className="btn-ghost w-full text-center"
                    href={rescueWhatsAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={openRescueWhatsApp}
                  >
                    {t("integrated.cancelRescueWhatsAppInstead")}
                  </a>
                </>
              )}
              <button
                type="button"
                className="btn-ghost w-full"
                disabled={submitting}
                onClick={declineRescue}
              >
                {t("integrated.cancelRescueContinue")}
              </button>
            </div>
            {error && <p className="text-sm text-danger">{error}</p>}
            <p className="text-xs text-ink-2">{t("integrated.cancelRescueFootnote")}</p>
          </>
        )}

        {(stage === "slots" || stage === "reschedule") && !pendingSlot && (
          <>
            <div className="text-lg font-semibold pr-8">
              {stage === "reschedule"
                ? t("integrated.cancelRescheduleTitle")
                : t("integrated.cancelSlotsTitle")}
            </div>
            <p className="text-sm text-ink-2">{t("integrated.cancelSlotsSubtitle")}</p>
            {days.length === 0 ? (
              <p className="text-sm text-ink-2">
                {refundOffer && stage === "slots"
                  ? t("integrated.cancelSlotsEmptyRefundWindow", { deadline: offerDeadlineLabel })
                  : t("integrated.cancelSlotsEmpty")}
              </p>
            ) : (
              <div className="space-y-4">
                {days.map((day) => (
                  <div key={day.key} className="space-y-2">
                    <div className="text-sm font-semibold capitalize">{day.label}</div>
                    <div className="flex flex-wrap gap-2">
                      {day.slots.map((slot) => (
                        <button
                          key={slot.id}
                          type="button"
                          className="btn-ghost text-sm px-3 py-1.5 border border-[color:var(--border)] rounded-md"
                          disabled={submitting}
                          onClick={() => setPendingSlot(slot)}
                        >
                          {formatSlotClock(slot.starts_at, locale)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {error && <p className="text-sm text-danger">{error}</p>}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="btn-ghost"
                disabled={submitting}
                onClick={() => {
                  if (intent === "reschedule") {
                    onClose();
                    return;
                  }
                  setError(null);
                  setStage(stage === "reschedule" ? "manage_booking" : slotsReturnStage);
                }}
              >
                {t("integrated.cancelSlotsBack")}
              </button>
              {stage === "slots" && (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => {
                    if (slotsReturnStage === "confirm") {
                      // Came from the card on the confirm screen: back to it, nothing declined yet.
                      setError(null);
                      setStage("confirm");
                      return;
                    }
                    declineRescue();
                  }}
                >
                  {t("integrated.cancelRescueContinue")}
                </button>
              )}
            </div>
          </>
        )}

        {(stage === "slots" || stage === "reschedule") && pendingSlot && (
          <>
            <div className="text-lg font-semibold pr-8">{t("integrated.cancelSlotsConfirm")}</div>
            <p className="text-sm font-medium">
              {formatSupportSlotTime(pendingSlot.starts_at, locale)}
            </p>
            {error && <p className="text-sm text-danger">{error}</p>}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="btn-ghost"
                disabled={submitting}
                onClick={() => {
                  setError(null);
                  setPendingSlot(null);
                }}
              >
                {t("integrated.cancelSlotsBack")}
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={submitting}
                onClick={() => void confirmSlot()}
              >
                {submitting ? t("integrated.cancelBusy") : t("integrated.cancelSlotsConfirmYes")}
              </button>
            </div>
          </>
        )}

        {stage === "booked" && booking && (
          <>
            <div className="text-lg font-semibold pr-8">{t("integrated.cancelBookedTitle")}</div>
            <p className="text-sm text-ink-2">{t("integrated.cancelBookedBody")}</p>
            <p className="text-sm font-medium">
              {formatSupportSlotTime(booking.starts_at, locale)}
            </p>
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn-primary" onClick={onClose}>
                {t("integrated.cancelBookedClose")}
              </button>
            </div>
          </>
        )}

        {stage === "reason" && (
          <>
            <div className="text-lg font-semibold pr-8">{t("integrated.cancelReasonTitle")}</div>
            <p className="text-sm text-ink-2">{t("integrated.cancelReasonSubtitle")}</p>
            <div className="space-y-2">
              {CANCELLATION_REASONS.map((id) => (
                <div key={id}>
                  <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-md hover:bg-black/5">
                    <input
                      type="radio"
                      name="cancellation_reason"
                      className="mt-0.5"
                      checked={reason === id}
                      onChange={() => {
                        setReason(id);
                        setSubreasons([]);
                        setPlanTarget(leadsToPlanPicker(id) ? (planTargets[0] ?? null) : null);
                        setError(null);
                      }}
                    />
                    <span>{t(`integrated.cancelReason_${id}`)}</span>
                  </label>
                  {reason === id && subreasonsFor(id).length > 0 && (
                    <fieldset className="ml-7 mt-1 space-y-1">
                      <legend className="text-xs text-ink-2 mb-1">{t("integrated.cancelSubreasonsLabel")}</legend>
                      {subreasonsFor(id).map((sub) => (
                        <label key={sub} className="flex items-start gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={subreasons.includes(sub)}
                            onChange={(e) =>
                              setSubreasons((prev) =>
                                normalizeSubreasons(
                                  id,
                                  e.target.checked ? [...prev, sub] : prev.filter((x) => x !== sub),
                                ),
                              )
                            }
                          />
                          <span>{t(`integrated.cancelSubreason_${id}_${sub}`)}</span>
                        </label>
                      ))}
                    </fieldset>
                  )}
                  {reason === id && reasonAction === "support" && (
                    <p className="ml-7 mt-1 text-xs text-ink-2">{t("integrated.cancelChangePlanSupportBody")}</p>
                  )}
                  {reason === id && reasonAction === "plan_picker" && (
                    <fieldset className="ml-7 mt-1 space-y-1">
                      <legend className="text-xs text-ink-2 mb-1">
                        {planChange.kind === "reactivate"
                          ? t("integrated.cancelChangePlanReactivateLabel")
                          : t("integrated.cancelChangePlanTargetLabel")}
                      </legend>
                      {planTargets.map((target) => (
                        <label
                          key={`${target.plan}_${target.interval}`}
                          className="flex items-start gap-2 text-sm cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="cancellation_change_plan_target"
                            className="mt-0.5"
                            checked={planTarget?.plan === target.plan && planTarget.interval === target.interval}
                            onChange={() => setPlanTarget(target)}
                          />
                          <span>
                            {t(`plans.${target.plan}`)} {t(`plans.${target.interval}`).toLowerCase()}
                          </span>
                        </label>
                      ))}
                    </fieldset>
                  )}
                </div>
              ))}
            </div>
            {error && <p className="text-sm text-danger">{error}</p>}
            {reasonAction !== "continue" ? (
              <div className="flex flex-wrap gap-2 justify-end">
                <button type="button" className="btn-ghost" onClick={continueFromReason}>
                  {t("integrated.cancelRescueContinue")}
                </button>
                {reasonAction === "support" ? (
                  <a
                    className="btn-primary text-center"
                    href={planSupportWhatsAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={openPlanSupportWhatsApp}
                  >
                    {t("integrated.cancelRescueWhatsAppCta")}
                  </a>
                ) : (
                  <button type="button" className="btn-primary" onClick={goToPlanPicker} disabled={!planTarget}>
                    {t("integrated.cancelChangePlanCta")}
                  </button>
                )}
              </div>
            ) : (
              <div className="flex gap-2 justify-end">
                <button type="button" className="btn-ghost" onClick={onClose}>
                  {t("common.back")}
                </button>
                <button type="button" className="btn-primary" onClick={continueFromReason}>
                  {t("integrated.cancelReasonContinue")}
                </button>
              </div>
            )}
          </>
        )}

        {stage === "confirm" && giftState === "accepted" && (
          <>
            <div className="text-lg font-semibold pr-8">{t("integrated.retentionGiftAcceptedTitle")}</div>
            <p className="text-sm text-ink-2 leading-relaxed">
              {t("integrated.retentionGiftAcceptedBody", {
                name: retentionOffer?.name ?? "Zenlinks",
                date: giftUntil ?? "",
              })}
            </p>
            <button type="button" className="btn-primary w-full" onClick={onClose}>
              {t("integrated.retentionGiftAcceptedClose")}
            </button>
          </>
        )}

        {stage === "confirm" && giftState !== "accepted" && (
          <>
            <div className="text-lg font-semibold pr-8">{t("integrated.cancelConfirmTitle")}</div>
            {showCheaper && cheaperOffers && (
              <PriceOfferCard
                offers={cheaperOffers}
                stone={cheaperStone}
                gift={showGift ? retentionOffer : null}
                giftBusy={giftState === "accepting"}
                giftError={giftError}
                currentPlanLabel={
                  cheaperPlanCurrent
                    ? `${t(`plans.${cheaperPlanCurrent.plan}`)} ${t(`plans.${cheaperPlanCurrent.interval}`).toLowerCase()}`
                    : ""
                }
                paymentMethod={cheaperPlanCurrent?.paymentMethod ?? null}
                inRefundWindow={refundOffer != null}
                money={money}
                disabled={submitting}
                onPick={pickCheaperPlan}
                onOnlyGift={() => void acceptGift()}
              />
            )}
            {showGift && !showCheaper && retentionOffer && (
              <div className="rounded-lg border border-purple/40 bg-purple/[0.08] p-4 space-y-3">
                <div className="font-semibold text-sm">
                  {t("integrated.retentionGiftTitle", {
                    name: retentionOffer.name,
                    months: retentionOffer.months,
                  })}
                </div>
                <p className="text-sm text-ink-2 leading-relaxed">
                  {t("integrated.retentionGiftBody", { months: retentionOffer.months })}
                </p>
                {giftError && <p className="text-sm text-danger">{giftError}</p>}
                <button
                  type="button"
                  className="btn-primary w-full"
                  disabled={giftState === "accepting" || submitting}
                  onClick={() => void acceptGift()}
                >
                  {giftState === "accepting"
                    ? t("integrated.retentionGiftAccepting")
                    : t("integrated.retentionGiftAccept")}
                </button>
              </div>
            )}
            {showSpecialist && (
              <div className="rounded-lg border border-purple/40 bg-purple/[0.08] p-4 space-y-3">
                <div className="font-semibold text-sm">{t("integrated.refundWindowSpecialistTitle")}</div>
                <p className="text-sm text-ink-2 leading-relaxed">
                  {t("integrated.refundWindowSpecialistBody", { deadline: offerDeadlineLabel })}
                </p>
                <button
                  type="button"
                  className="btn-primary w-full"
                  disabled={submitting}
                  onClick={() => void openSlots("slots", "confirm")}
                >
                  {submitting ? t("integrated.cancelBusy") : t("integrated.refundWindowSpecialistCta")}
                </button>
              </div>
            )}
            {losses && (
              <div className="rounded-lg border border-warning/40 bg-warning/[0.08] p-4">
                <div className="font-semibold text-sm">{losses.title}</div>
                <ul className="mt-2 space-y-1 text-sm text-ink-2 leading-relaxed list-disc pl-5">
                  {losses.items.map((item) => (
                    <li key={item.key} className="break-words">
                      {item.label}
                      {item.detail && <div className="text-xs text-ink-2 mt-0.5">{item.detail}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {refundOffer ? (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium mb-2">{t("integrated.cancelChoiceTitle")}</legend>
                <CancelChoiceOption
                  checked={choice === "stop_renewal"}
                  highlighted
                  onSelect={() => {
                    setChoice("stop_renewal");
                    setError(null);
                  }}
                  title={t("integrated.cancelChoiceStopRenewalTitle", { date: periodEndLabel })}
                  body={t("integrated.cancelChoiceStopRenewalBody", { deadline: offerDeadlineLabel })}
                />
                <CancelChoiceOption
                  checked={choice === "refund_now"}
                  onSelect={() => {
                    setChoice("refund_now");
                    setError(null);
                  }}
                  title={t("integrated.cancelChoiceRefundTitle", { amount: offerAmountLabel })}
                  body={t("integrated.cancelChoiceRefundBody")}
                />
              </fieldset>
            ) : subscriptionOver ? (
              <p className="text-sm text-ink-2">{t("integrated.cancelExpiredStopsChargeOnly")}</p>
            ) : (
              periodEndLabel && (
                <p className="text-sm text-ink-2">{t("integrated.cancelStopRenewalSummary", { date: periodEndLabel })}</p>
              )
            )}
            <label className="block space-y-1">
              <span className="text-sm text-ink-2">{t("integrated.cancelReasonDetailsRequired")}</span>
              <textarea
                className="input min-h-[72px]"
                value={details}
                maxLength={1000}
                required
                onChange={(e) => {
                  setDetails(e.target.value);
                  if (error) setError(null);
                }}
              />
            </label>
            <div className="text-sm font-medium">{t("integrated.cancelTypeConfirmation")}</div>
            <input
              className="input"
              placeholder="CANCELAR"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
            />
            {effectiveChoice === "refund_now" && (
              <p className="text-sm rounded-md border border-danger/40 bg-danger/[0.06] p-3" role="alert">
                {t("integrated.cancelChoiceRefundWarning", { amount: offerAmountLabel })}
              </p>
            )}
            {error && <p className="text-sm text-danger">{error}</p>}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setError(null);
                  // Back to the reason, so a wrong pick can be changed; continuing from there
                  // shows the help again and records a fresh viewed event.
                  setStage("reason");
                }}
                disabled={submitting}
              >
                {t("common.back")}
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={submitting || phrase.trim().toUpperCase() !== "CANCELAR"}
                onClick={() => {
                  if (!hasCancellationDetails(details)) {
                    setError(t("integrated.cancelReasonDetailsMissing"));
                    return;
                  }
                  void confirmCancel();
                }}
              >
                {submitting ? t("integrated.processing") : t("common.confirm")}
              </button>
            </div>
          </>
        )}

        {stage === "done" && (
          <>
            <div className="text-lg font-semibold">
              {refundAmountLabel
                ? t("integrated.cancelRefundDone", { amount: refundAmountLabel })
                : stoppedWithOffer
                  ? t("integrated.cancelStopRenewalDoneWithRefund", {
                      date,
                      amount: formatRefundMoney(stoppedWithOffer.amount_cents, stoppedWithOffer, i18n.language),
                      deadline: formatRefundDeadline(stoppedWithOffer.refundable_until, t, i18n.language),
                    })
                  : date
                    ? t("integrated.cancelDone", { date })
                    : expiredStopConfirmed
                      ? t("integrated.cancelExpiredDone")
                      : t("integrated.cancelExpiredRecorded")}
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn-primary" onClick={() => closeFinalScreen(onClose)}>
                {t("common.continue")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** "8 de outubro às 14:32": the refund window ends mid-day, so the hour matters. */
function formatRefundDeadline(iso: string, t: (key: string, opts?: Record<string, string>) => string, locale: string) {
  return t("integrated.dateAtTime", formatBillingDayAndTime(iso, locale));
}

/**
 * Continue on a cancellation's final screen: the app keeps its sheet open after a cancellation so
 * this screen can be read, so ask it to close now. Standalone, it only closes the dialog.
 */
function closeFinalScreen(onClose: () => void) {
  onClose();
  if (isEmbeddedBillingContext()) notifyParentBillingClose();
}

/** One cheaper way to stay (PIT-452): what it costs, from when, and what changes. */
/**
 * "Está caro" as one offer (owner's mock, 16/10): the cheapest option leads (the annual in 12x when
 * the session offers it, else the Basic monthly), the Basic follows as the alternative, and the
 * Zenlinks gift comes with either when Zenpost offers it, granted once the change is accepted. The
 * gift alone stays one link away.
 */
function PriceOfferCard({
  offers,
  stone,
  gift,
  giftBusy,
  giftError,
  currentPlanLabel,
  paymentMethod,
  inRefundWindow,
  money,
  disabled,
  onPick,
  onOnlyGift,
}: {
  offers: ChangePreview[];
  /** The annual option's 12x figures (the annual is only offered in 12x). */
  stone: StoneInstallments | null;
  /** The gift that comes with the plan; null when Zenpost does not offer it. */
  gift: RetentionOffer | null;
  giftBusy: boolean;
  giftError: string | null;
  currentPlanLabel: string;
  paymentMethod: PaymentMethod | null;
  /** The annual charge is a second paid charge, and only the first one is ever refunded. */
  inRefundWindow: boolean;
  money: (cents: number) => string;
  disabled: boolean;
  onPick: (target: CheaperPlanTarget, withGift: boolean) => void;
  onOnlyGift: () => void;
}) {
  const { t, i18n } = useTranslation();
  const annual = stone ? offers.find(isInstallmentOffer) : undefined;
  const basic = offers.find((o) => !isInstallmentOffer(o));
  const lead = annual ?? basic;
  if (!lead) return null;
  const withGift = gift != null;
  const giftLine = gift ? (
    <div className="flex items-center gap-2 rounded-lg border border-dashed border-emerald-500 bg-emerald-50 px-3 py-2 text-[13.5px] text-emerald-900">
      <Gift className="w-5 h-5 shrink-0 text-emerald-600" aria-hidden="true" />
      <span className="font-semibold">{t("integrated.priceOfferGiftLine", { name: gift.name, months: gift.months })}</span>
    </div>
  ) : null;
  const lost = t("plansPage.premiumHighlights", { returnObjects: true }) as unknown;

  const basicBody = (offer: ChangePreview) => (
    <>
      <p className="text-sm text-ink-2 leading-relaxed">
        {t("integrated.cheaperPlanBasicBody", {
          date: offer.effectiveAt ? formatBillingDate(offer.effectiveAt, i18n.language) : "",
        })}
      </p>
      {Array.isArray(lost) && lost.length > 0 && (
        <ul className="text-sm text-ink-2 leading-relaxed list-disc pl-5">
          {lost.map((line) => (
            <li key={String(line)}>{String(line)}</li>
          ))}
        </ul>
      )}
      {paymentMethod === "openpix" && offer.requiresProviderApproval && (
        <p className="text-sm text-ink-2 leading-relaxed">{t("integrated.cheaperPlanBasicPixNote")}</p>
      )}
    </>
  );
  const pickBasic = (offer: ChangePreview) =>
    onPick({ plan: offer.targetPlan, interval: offer.targetInterval }, withGift);

  return (
    <div className="rounded-2xl p-[2px] shadow-[0_18px_40px_-22px_rgba(124,92,255,0.75)]" style={{ background: "var(--grad-primary)" }}>
      <div className="rounded-[14px] bg-gradient-to-b from-[#f6f1ff] to-white p-4 space-y-3.5">
        <span
          className="inline-block rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white"
          style={{ background: "var(--grad-primary)" }}
        >
          {t("integrated.priceOfferRibbon")}
        </span>
        <div className="text-lg font-bold leading-snug">
          {gift ? t("integrated.priceOfferTitleGift", { name: gift.name }) : t("integrated.cheaperPlanTitle")}
        </div>

        <div className="relative rounded-xl border-[1.5px] border-[var(--purple-mid)] bg-white p-3.5 space-y-2">
          {annual && stone ? (
            <>
              <span className="absolute -top-2.5 right-3 rounded-full bg-emerald-500 px-2 py-0.5 text-[11px] font-semibold text-white">
                {t("integrated.priceOfferBadge")}
              </span>
              <div className="text-[28px] font-bold leading-tight tabular-nums">
                {t("integrated.stoneChangeInstallment", { count: stone.count, installment: money(stone.first_year_installment_cents) })}{" "}
                <span className="text-sm font-medium text-[var(--purple-deep)]">{t("integrated.priceOfferNoInterest")}</span>
              </div>
              <p className="text-sm text-ink-2 leading-relaxed">
                {t("integrated.priceOfferAnnualBody", {
                  plan: t(`plans.${annual.targetPlan}`),
                  total: money(stone.first_year_cents),
                  renewal: money(stone.renewal_installment_cents),
                })}
              </p>
              {inRefundWindow && (
                <p className="text-sm text-ink-2 leading-relaxed">{t("integrated.cheaperPlanAnnualRefundNote")}</p>
              )}
              {giftLine}
              <button
                type="button"
                className="btn-primary w-full"
                disabled={disabled}
                onClick={() => onPick({ plan: annual.targetPlan, interval: "annual", paymentMethod: "stone" }, withGift)}
              >
                {withGift ? t("integrated.priceOfferAnnualCtaGift") : t("integrated.cheaperPlanAnnualCta")}
              </button>
            </>
          ) : (
            <>
              <div className="text-[22px] font-bold leading-tight">
                {t("integrated.cheaperPlanBasicTitle", { plan: t(`plans.${lead.targetPlan}`), price: money(lead.recurringPriceCents) })}
              </div>
              {basicBody(lead)}
              {giftLine}
              <button type="button" className="btn-primary w-full" disabled={disabled} onClick={() => pickBasic(lead)}>
                {withGift
                  ? t("integrated.priceOfferBasicCtaGift", { plan: t(`plans.${lead.targetPlan}`) })
                  : t("integrated.cheaperPlanBasicCta", { plan: t(`plans.${lead.targetPlan}`) })}
              </button>
            </>
          )}
        </div>

        {annual && stone && basic && (
          <>
            <div className="flex items-center gap-2.5 text-xs uppercase tracking-wider text-ink-3">
              <span className="h-px flex-1 bg-black/10" />
              {t("integrated.priceOfferOr")}
              <span className="h-px flex-1 bg-black/10" />
            </div>
            <div className="space-y-2">
              <div className="text-[15px] font-semibold">
                {t("integrated.cheaperPlanBasicTitle", { plan: t(`plans.${basic.targetPlan}`), price: money(basic.recurringPriceCents) })}
              </div>
              {basicBody(basic)}
              {giftLine}
              <button type="button" className="btn-ghost w-full" disabled={disabled} onClick={() => pickBasic(basic)}>
                {withGift
                  ? t("integrated.priceOfferBasicCtaGift", { plan: t(`plans.${basic.targetPlan}`) })
                  : t("integrated.cheaperPlanBasicCta", { plan: t(`plans.${basic.targetPlan}`) })}
              </button>
            </div>
          </>
        )}

        {gift && (
          <div className="space-y-1 text-center">
            <button
              type="button"
              className="text-sm font-medium text-[var(--purple-deep)] underline underline-offset-4 disabled:opacity-60"
              disabled={disabled || giftBusy}
              onClick={onOnlyGift}
            >
              {giftBusy ? t("integrated.retentionGiftAccepting") : t("integrated.priceOfferOnlyGift", { plan: currentPlanLabel })}
            </button>
            {giftError && <p className="text-sm text-danger">{giftError}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function CancelChoiceOption({
  checked,
  highlighted = false,
  onSelect,
  title,
  body,
}: {
  checked: boolean;
  highlighted?: boolean;
  onSelect: () => void;
  title: string;
  body: string;
}) {
  return (
    <label
      className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer text-sm ${
        checked ? "border-purple bg-purple/[0.06]" : "border-black/10 hover:bg-black/5"
      } ${highlighted ? "font-medium" : ""}`}
    >
      <input type="radio" name="cancellation_choice" className="mt-0.5" checked={checked} onChange={onSelect} />
      <span className="space-y-1">
        <span className="block">{title}</span>
        <span className="block text-xs text-ink-2 font-normal leading-relaxed">{body}</span>
      </span>
    </label>
  );
}

/** The money back after stopping the renewal, inside the same 7-day window. Access ends now. */
function LateRefundDialog({
  token,
  offer,
  periodEnd,
  onClose,
  onDone,
  onWindowClosed,
}: {
  token: string;
  offer: CancellationRefundOffer;
  periodEnd: string | null;
  onClose: () => void;
  onDone: () => void | Promise<void>;
  onWindowClosed: () => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refundedLabel, setRefundedLabel] = useState<string | null>(null);
  const [windowClosed, setWindowClosed] = useState(false);
  const amount = formatRefundMoney(offer.amount_cents, offer, i18n.language);

  async function confirm() {
    setSubmitting(true);
    setError(null);
    try {
      const r = await api<{ refund_amount_cents: number }>("/api/integrated/cancel/refund", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      setRefundedLabel(formatRefundMoney(r.refund_amount_cents, offer, i18n.language));
      await onDone();
    } catch (err) {
      const code = err instanceof ApiError ? apiErrorCode(err) : undefined;
      if (code === "refund_not_eligible") {
        setWindowClosed(true);
        setError(
          t("integrated.cancelRefundWindowClosed", {
            date: periodEnd ? formatBillingDate(periodEnd, i18n.language) : "",
          }),
        );
        await onWindowClosed();
      } else if (code === "support_booking_active") {
        setError(t("integrated.lateRefundBookingBlocked"));
      } else {
        setError(t("integrated.changeFailed"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="card-modal max-w-md w-full p-6 space-y-4 text-[color:var(--text-primary)]">
        {refundedLabel ? (
          <>
            <div className="text-lg font-semibold">{t("integrated.lateRefundDone", { amount: refundedLabel })}</div>
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn-primary" onClick={() => closeFinalScreen(onClose)}>
                {t("common.continue")}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-lg font-semibold">{t("integrated.lateRefundTitle")}</div>
            <p className="text-sm text-ink-2 leading-relaxed">{t("integrated.lateRefundBody", { amount })}</p>
            {error && <p className="text-sm text-danger">{error}</p>}
            <div className="flex gap-2 justify-end pt-1">
              <button type="button" className="btn-ghost" onClick={onClose} disabled={submitting}>
                {t("common.back")}
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={submitting || windowClosed}
                onClick={() => void confirm()}
              >
                {submitting ? t("integrated.processing") : t("integrated.lateRefundConfirm")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex items-center justify-center p-4 surface-aurora">
      <div className="card-modal max-w-md w-full p-8 text-center text-[color:var(--text-primary)]">{children}</div>
    </main>
  );
}

/** The method selector offers card and PIX; a Stone subscription is a card. */
function selectableMethod(method: PaymentMethod): PaymentMethod {
  return method === "stone" ? "stripe" : method;
}
